import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { jsPDF } from 'jspdf';
import { getCampStatus } from '../../utils/campLogic';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

const STEWARDSHIP_SURVEY = [
  {
    id: 'christian',
    label: 'The Christian · Mindset & Calling',
    desc: 'How clearly are you writing from your calling rather than fear or comparison?',
    qs: [
      { id: 'q1', text: 'I write from a clear sense of calling rather than platform pressure or comparison.' },
      { id: 'q2', text: 'When fear or doubt shows up, I return to my gospel motivation rather than letting it stop my work.' },
      { id: 'q3', text: 'I regularly reflect on who I\'m writing for and why — beyond sales or recognition.' },
    ]
  },
  {
    id: 'creator',
    label: 'The Creator · Writing & Finishing',
    desc: 'Are you consistently putting words on the page and completing what you start?',
    qs: [
      { id: 'q4', text: 'I write consistently — not just when inspired — and make regular progress on my manuscript.' },
      { id: 'q5', text: 'I finish what I start. Drafts in my life become completed manuscripts.' },
      { id: 'q6', text: 'I invest in my craft through courses, feedback, or deliberate practice.' },
    ]
  },
  {
    id: 'caretaker',
    label: 'The Caretaker · Editing & Publishing',
    desc: 'Are your finished drafts becoming published books — or sitting unedited on your hard drive?',
    qs: [
      { id: 'q7', text: 'I take my finished drafts through a real editing process before publishing.' },
      { id: 'q8', text: 'I have a clear publishing plan for my work — or I\'m actively building one.' },
      { id: 'q9', text: 'I treat my author work as a stewardship worth managing, not just a creative outlet.' },
    ]
  },
  {
    id: 'connector',
    label: 'The Connector · Marketing & Reaching Readers',
    desc: 'Are you deliberately connecting your book to the readers who need it?',
    qs: [
      { id: 'q10', text: 'I regularly tell people about my book through email, social media, ads, or other channels.' },
      { id: 'q11', text: 'I have a consistent way of reaching new readers — not just waiting for them to find me.' },
      { id: 'q12', text: 'I think of marketing as serving my reader, not self-promotion.' },
    ]
  }
];

const SURVEY_RESULTS = {
  christian: {
    name: 'The Christian',
    strong: 'Your foundational motivation is strong. You are clearly writing from a deep sense of calling rather than seeking worldly validation.',
    weak: 'You might be struggling with comparison or losing sight of your primary "why". Take time this week to write down your core gospel motivation for your current project.',
    link: 'GospelWise Author Handbook',
    url: 'https://gospelwiseauthor.com/checkout/e3wuerjixky4ol5'
  },
  creator: {
    name: 'The Creator',
    strong: 'You are a master of making time for your craft. Your dedication to consistently putting words on the page is bearing fruit.',
    weak: 'You have a tendency to start projects but struggle to see them through to completion. Commit to a realistic, daily word count goal for the next 14 days and stick to it.',
    link: 'One Draft Wonder',
    url: 'http://gospelwiseauthor.com/course/one-draft-wonder'
  },
  caretaker: {
    name: 'The Caretaker',
    strong: 'You treat your work with the respect it deserves. Your commitment to editing and professional publishing sets your books apart.',
    weak: 'Your drafts might be piling up without receiving the polish they need to be truly impactful. Schedule a dedicated block of time next week solely for editing your most complete manuscript.',
    link: 'Write. Refine. Release. Worshop',
    url: 'https://gospelwiseauthor.com/course/write-refine-release'
  },
  connector: {
    name: 'The Connector',
    strong: 'You understand that marketing is a form of service. You are effectively bridging the gap between your message and the readers who need it.',
    weak: 'You are writing great content but hesitating to share it with the world. Choose one channel (email or social media) and share a brief, honest update about your book this week.',
    link: 'GospelWise Author Marketing Essentials',
    url: 'https://gospelwiseauthor.com/course/marketing-essentials'
  }
};

export default function Cabin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [saveStatus, setSaveStatus] = useState('');
  const [devMode, setDevMode] = useState(false);
  
  // UI States
  const [isScrolled, setIsScrolled] = useState(false);
  const topRef = useRef(null);

  // Survey States
  const [surveyStep, setSurveyStep] = useState(1);
  const [retakeSurvey, setRetakeSurvey] = useState(false);
  const [creatorSurveyStep, setCreatorSurveyStep] = useState(0);
  const [caretakerSurveyStep, setCaretakerSurveyStep] = useState(0);
  
  // Marketing Survey States
  const [marketingSurveyStep, setMarketingSurveyStep] = useState(0); 

  const [formData, setFormData] = useState({
    1: {}, 2: {}, 3: { formats: [] }, 4: {}, 5: {}
  });

  const formDataRef = useRef(formData);
  const activeDayRef = useRef(activeDay);

  const { isAAP, currentTrailDay, windowOpen } = getCampStatus(user, settings);
  const isLockedOut = !windowOpen;

  useEffect(() => { activeDayRef.current = activeDay; }, [activeDay]);

  // Robust Scroll Detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: '-10px 0px 0px 0px' }
    );

    if (topRef.current) observer.observe(topRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (day, field, value) => {
    const nextData = {
      ...formDataRef.current,
      [day]: { ...formDataRef.current[day], [field]: value }
    };
    formDataRef.current = nextData;
    setFormData(nextData);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const setRes = await api.get('settings_fbwm');
        const settingsData = Array.isArray(setRes) ? setRes : setRes.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          const key = s.setting_key || s.key;
          if (key) sMap[key] = s.value;
        });
        setSettings(sMap);

        const res = await api.get('compass_entries_fbwm', { user_id: user.id });
        const rawItems = Array.isArray(res) ? res : res.data || [];
        const items = rawItems.filter(item => item.user_id === user.id);

        const loaded = { 1: {}, 2: {}, 3: { formats: [] }, 4: {}, 5: {} };
        const sortedItems = [...items].sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));

        sortedItems.forEach(item => {
          let dayNum = item.day || (item.section?.startsWith('day_') ? parseInt(item.section.replace('day_', ''), 10) : null);
          dayNum = parseInt(dayNum, 10);

          if (dayNum >= 1 && dayNum <= 5) {
            const parsed = JSON.parse(item.response_text || '{}');
            if (dayNum === 4 && !parsed.reader_name && !parsed.reader_life && parsed.reader_desc) parsed.reader_life = parsed.reader_desc;
            if (dayNum === 3 && !Array.isArray(parsed.formats)) parsed.formats = [];
            loaded[dayNum] = { ...loaded[dayNum], ...parsed };
          }
        });
        
        formDataRef.current = loaded;
        setFormData(loaded);
      } catch (err) {
        console.error("Failed to load workbook data", err);
      } finally {
        setLoading(false);
      }
    };
    if (user) loadData();
  }, [user]);

  // Auto-Save Interval (30s)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) handleSave(activeDayRef.current, true);
    }, 30000);
    return () => clearInterval(interval);
  }, [loading]);

  const isDayLocked = (dayIndex) => {
    if (devMode) return false;
    if (user?.early_access == 1) return false;
    return currentTrailDay < dayIndex;
  };

  const handleSave = async (day, isBackground = false) => {
    if (!isBackground) setSaveStatus('Saving...');
    try {
      const res = await api.get('compass_entries_fbwm', { user_id: user.id });
      const rawItems = Array.isArray(res) ? res : res.data || [];
      const items = rawItems.filter(item => item.user_id === user.id);
      const existing = items.find(item => item.section === `day_${day}` || parseInt(item.day, 10) === parseInt(day, 10));

      const existingData = existing ? JSON.parse(existing.response_text || '{}') : {};
      const merged = { ...existingData, ...formDataRef.current[day] };
      const formattedDate = new Date().toISOString().slice(0, 19).replace('T', ' ');

      const payload = {
        section: `day_${day}`,
        day: parseInt(day, 10),
        response_text: JSON.stringify(merged),
        updated_at: formattedDate,
        owner_id: user.id,
        user_id: user.id
      };

      if (existing && existing.id) {
        await api.put('compass_entries_fbwm', existing.id, payload);
      } else {
        await api.post('compass_entries_fbwm', payload);
      }

      const time = new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
      setSaveStatus(`Saved ${time}`);
      if (!isBackground) setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      console.error('Save error', err);
      setSaveStatus('Error saving');
    }
  };

  // --- SURVEY LOGIC ---
  const calculateSurveyResults = async () => {
    const d = formData[1] || {};
    const scores = {
      christian: (parseInt(d.q1)||0) + (parseInt(d.q2)||0) + (parseInt(d.q3)||0),
      creator: (parseInt(d.q4)||0) + (parseInt(d.q5)||0) + (parseInt(d.q6)||0),
      caretaker: (parseInt(d.q7)||0) + (parseInt(d.q8)||0) + (parseInt(d.q9)||0),
      connector: (parseInt(d.q10)||0) + (parseInt(d.q11)||0) + (parseInt(d.q12)||0),
    };

    let maxScore = -1;
    let minScore = 999;
    let strongest = 'christian';
    let weakest = 'christian';

    Object.entries(scores).forEach(([key, val]) => {
      if (val > maxScore) { maxScore = val; strongest = key; }
      if (val < minScore) { minScore = val; weakest = key; }
    });
    
    // Save to formData
    const updates = {
      survey_christian_score: scores.christian,
      survey_creator_score: scores.creator,
      survey_caretaker_score: scores.caretaker,
      survey_connector_score: scores.connector,
      survey_strongest: strongest,
      survey_weakest: weakest,
      survey_completed: true
    };
    
    const nextData = {
      ...formDataRef.current,
      1: { ...formDataRef.current[1], ...updates }
    };
    formDataRef.current = nextData;
    setFormData(nextData);
    
    // Save to DB
    await handleSave(1);
    
    // Switch UI to results step
    setSurveyStep(5);
    setRetakeSurvey(false);

    // Silently fire webhook in background
    submitSurveyWebhook(scores, strongest, weakest, nextData[1]);
  };

  const submitSurveyWebhook = async (scores, strongest, weakest, day1Data) => {
    try {
      const payload = {
        email: user?.email,
        name: user?.name,
        scores: scores,
        strongest: strongest,
        weakest: weakest,
        answers: {
          q1: day1Data?.q1, q2: day1Data?.q2, q3: day1Data?.q3,
          q4: day1Data?.q4, q5: day1Data?.q5, q6: day1Data?.q6,
          q7: day1Data?.q7, q8: day1Data?.q8, q9: day1Data?.q9,
          q10: day1Data?.q10, q11: day1Data?.q11, q12: day1Data?.q12,
        }
      };
      
      await fetch('/webhook-receiver.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.error('Webhook silent error', e);
    }
  };

  const getCompassSentences = () => {
    const d1 = formData[1] || {};
    const ce1 = `I am writing ${d1.ce_book || '[Not completed]'} for ${d1.ce_person || '[Not completed]'} because ${d1.ce_why || '[Not completed]'}.`;

    const d2 = formData[2] || {};
    const ce2 = `My manuscript is ${d2.ce_stage || '[Not completed]'}. My one writing move this week is ${d2.ce_action || '[Not completed]'}. I will write/edit ${d2.ce_amount || '[Not completed]'} by ${d2.ce_date || '[Not completed]'}.`;

    const d3 = formData[3] || {};
    const ce3 = `My publishing path is ${d3.ce_path || '[Not completed]'}. The one decision I need to make next is ${d3.ce_decision || '[Not completed]'}. My target window is ${d3.ce_timeframe || '[Not completed]'}.`;

    const d4 = formData[4] || {};
    const reader = d4.reader_name || d4.reader_desc || '[Not completed]';
    const ce4 = `I am writing for ${reader}. My first marketing move is ${d4.marketing_first_minimum || '[Not completed]'}. I will start by ${d4.marketing_first_content || '[Not completed]'}.`;

    return { ce1, ce2, ce3, ce4 };
  };

  const generateCompassPDF = () => {
    const doc = new jsPDF();
    const sentences = getCompassSentences();
    const d5 = formData[5] || {};

    // Page Background
    doc.setFillColor(253, 246, 236);
    doc.rect(0, 0, 210, 297, 'F');
    
    // Main Header
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(45, 80, 22);
    doc.setFontSize(24);
    doc.text("The GospelWise Author Compass", 105, 25, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(232, 119, 34);
    doc.text("CAMP FAIBOWRIMO · JULY 13–17, 2026", 105, 33, { align: 'center' });
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Prepared by ${user.name}`, 105, 39, { align: 'center' });
    doc.setDrawColor(232, 119, 34);
    doc.line(20, 45, 195, 45);

    let y = 55;

    // HELPER: Add Page
    const checkPageBreak = (neededHeight) => {
      if (y + neededHeight > 275) {
        doc.addPage();
        doc.setFillColor(253, 246, 236);
        doc.rect(0, 0, 210, 297, 'F');
        y = 20;
      }
    };

    // STEWARDSHIP PROFILE INJECTION (Moved to Top)
    if (formData[1]?.survey_completed) {
      const d1 = formData[1];
      const strongKey = d1.survey_strongest || 'christian';
      const weakKey = d1.survey_weakest || 'christian';

      checkPageBreak(120);

      doc.setFillColor(45, 80, 22);
      doc.rect(20, y, 175, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text("GOSPELWISE AUTHOR STEWARDSHIP PROFILE", 23, y + 5.5);
      y += 14;

      // Draw bars
      const scores = [
        { label: 'The Christian', score: d1.survey_christian_score || 0 },
        { label: 'The Creator', score: d1.survey_creator_score || 0 },
        { label: 'The Caretaker', score: d1.survey_caretaker_score || 0 },
        { label: 'The Connector', score: d1.survey_connector_score || 0 }
      ];

      scores.forEach(s => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(10);
        doc.text(`${s.label} (${s.score}/12)`, 20, y);
        y += 4;
        
        // Background bar
        doc.setFillColor(230, 230, 230);
        doc.rect(20, y, 100, 5, 'F');
        
        // Filled bar
        doc.setFillColor(37, 87, 171); // Blue
        if (s.label.toLowerCase().includes(weakKey)) {
          doc.setFillColor(232, 119, 34); // Orange for weakest
        }
        doc.rect(20, y, (s.score / 12) * 100, 5, 'F');
        y += 8;
      });
      y += 8;

      if (SURVEY_RESULTS[strongKey]) {
        checkPageBreak(30);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 87, 171); // Blue
        doc.setFontSize(10);
        doc.text(`Strongest Area: ${SURVEY_RESULTS[strongKey].name}`, 20, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        const sLines = doc.splitTextToSize(SURVEY_RESULTS[strongKey].strong, 170);
        doc.text(sLines, 20, y);
        y += (sLines.length * 5) + 5;
      }
      
      if (SURVEY_RESULTS[weakKey]) {
        checkPageBreak(30);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(232, 119, 34); // Orange
        doc.setFontSize(10);
        doc.text(`Growth Opportunity: ${SURVEY_RESULTS[weakKey].name}`, 20, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        const wLines = doc.splitTextToSize(SURVEY_RESULTS[weakKey].weak, 170);
        doc.text(wLines, 20, y);
        y += (wLines.length * 5) + 5;
      }
      
      doc.setDrawColor(232, 119, 34);
      doc.line(20, y, 195, y);
      y += 10;
    }

// HELPER: Add standard section
const addSection = (title, content, callout) => {
  // Measure body at its actual render size (12pt)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const lines = doc.splitTextToSize(content, 170);

  // Measure callout at its actual render size (10pt italic)
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  const calloutLines = doc.splitTextToSize(callout, 170);

  const estHeight = 14 + (lines.length * 5.5) + 6 + (calloutLines.length * 5) + 5;
  checkPageBreak(estHeight);

  doc.setFillColor(45, 80, 22);
  doc.rect(20, y, 175, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.text(title, 23, y + 5.5);

  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(12);
  doc.text(lines, 20, y);
  y += (lines.length * 5.5) + 6;

  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  doc.text(calloutLines, 20, y);
  y += (calloutLines.length * 5) + 5;
};

    addSection("THE CHRISTIAN — Calling Clarity", sentences.ce1, "When the writing gets hard, return here first. Your why is your fuel.");

    addSection("THE CREATOR — Writing Clarity", sentences.ce2, "Writers write. Finish the draft. Your voice is what your reader came for.");

    // CREATOR WRITING IDENTITY INJECTION
    if (formData[2]?.creator_survey_complete) {
      const d2 = formData[2];

      const BLOCK_LABELS = {
        'Perfectionism — I can\'t move forward until it\'s right': { label: 'Perfectionism', guidance: 'Finish before you polish. The rock tumbler smooths away the very thing that makes your voice worth reading.' },
        'Isolation — I don\'t know if it\'s any good': { label: 'Isolation', guidance: 'Your voice is what your reader came for. Find one trusted reader — not five. Community over committee.' },
        'Time — life keeps getting in the way': { label: 'Time', guidance: 'A sustainable rhythm beats an ambitious one. Even 20 minutes a day compounds into 120 hours a year.' },
        'Fear — what if no one reads it / what if they do': { label: 'Fear', guidance: 'The calling is bigger than the fear. Acts 20:24: finish the task the Lord Jesus has given you.' },
        'Clarity — I\'m not sure what I\'m trying to say': { label: 'Clarity', guidance: 'Write to find out what you think. The first draft is a discovery draft — clarity comes through writing, not before it.' },
        'Comparison — I feel behind other writers': { label: 'Comparison', guidance: 'You\'re not in a race. Write your book, not theirs. Comparison is the thief of creative output.' },
      };

      const STYLE_LABELS = {
        'Planner (outline everything before I write)': 'Plotter — structure first',
        'Pantser (discover the story as I write)': 'Pantser — discover as you write',
        'Hybrid (loose outline, fill it in as I go)': 'Hybrid — flexible framework',
        'Not sure yet': 'Still discovering your style',
      };

      checkPageBreak(14);
      y += 4;

      // Section header
      doc.setFillColor(45, 80, 22);
      doc.rect(20, y, 175, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text('YOUR WRITING IDENTITY PROFILE', 23, y + 5.5);
      y += 14;

      // Helper: amber sub-heading
      const drawCreatorHeading = (text) => {
        checkPageBreak(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(232, 119, 34);
        doc.setFontSize(10);
        doc.text(text, 20, y);
        y += 6;
      };

      // Helper: label + value row
      const drawCreatorRow = (label, value) => {
        if (!value || !String(value).trim()) return;
        const labelW = 48;
        const valueLines = doc.splitTextToSize(String(value), 175 - labelW - 4);
        const rowH = Math.max(6, valueLines.length * 5 + 2);
        checkPageBreak(rowH + 3);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 87, 171);
        doc.setFontSize(9);
        doc.text(label, 20, y + 4);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.text(valueLines, 20 + labelW, y + 4);
        y += rowH;
      };

      // Writing style
      drawCreatorHeading('Writing Style');
      const styleLabel = STYLE_LABELS[d2.creator_writing_style] || d2.creator_writing_style;
      drawCreatorRow('Your Approach:', styleLabel);
      y += 4;

      // Block type + guidance
      drawCreatorHeading('Your Writing Block');
      const blockData = BLOCK_LABELS[d2.creator_block_type];
      if (blockData) {
        drawCreatorRow('Block Type:', blockData.label);

        // Guidance box — amber-pale
        const guidanceLines = doc.splitTextToSize(blockData.guidance, 163);
        const boxH = guidanceLines.length * 5 + 10;
        checkPageBreak(boxH + 4);

        doc.setFillColor(254, 243, 232);
        doc.setDrawColor(232, 119, 34);
        doc.setLineWidth(0.5);
        doc.roundedRect(20, y, 175, boxH, 2, 2, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 80, 22);
        doc.setFontSize(9);
        doc.text('A word for you:', 24, y + 5.5);

        doc.setFont('helvetica', 'italic');
        doc.setTextColor(50, 50, 50);
        doc.text(guidanceLines, 24, y + 10.5);
        y += boxH + 6;
      } else {
        drawCreatorRow('Block Type:', d2.creator_block_type);
        y += 4;
      }

      // Completion style
      drawCreatorHeading('How You Finish');
      drawCreatorRow('Completion Style:', d2.creator_completion_style);
      y += 4;

      // Voice strength
      drawCreatorHeading('Your Voice Strength');
      drawCreatorRow('Strongest Gift:', d2.creator_voice_strength);
      y += 4;

      // Closing divider
      doc.setDrawColor(232, 119, 34);
      doc.line(20, y, 195, y);
      y += 10;
    }
    // END CREATOR WRITING IDENTITY INJECTION

    addSection("THE CARETAKER — Publishing Clarity", sentences.ce3, "You don't need anyone's permission to publish. You need a plan and the faithfulness to walk it.");

    // CARETAKER PUBLISHING READINESS INJECTION
    if (formData[3]?.caretaker_survey_complete) {
      const d3 = formData[3];

      const FEAR_LABELS = {
        'Cost — I\'m not sure I can afford editing, cover design, etc.': {
          label: 'Cost',
          guidance: 'Start with what you can: a professional cover is the highest-ROI investment. Build quality over time. A great cover and clean formatting are 90% of perceived professionalism.',
        },
        'Quality — what if my book doesn\'t look professional?': {
          label: 'Quality',
          guidance: 'A professional cover and clean interior formatting are achievable at reasonable cost. Readers judge books by covers — invest there first.',
        },
        'Complexity — the process feels overwhelming': {
          label: 'Complexity',
          guidance: 'Break it into three decisions: path, format, platform. You don\'t have to figure out everything at once. One faithful step at a time.',
        },
        'Legitimacy — will people take a self-published book seriously?': {
          label: 'Legitimacy',
          guidance: 'The legitimacy question is settled. What makes a book credible is quality — not the publisher\'s logo. Your imprint is as real as any.',
        },
        'Rights — I\'m confused about ISBNs, copyright, distribution': {
          label: 'Rights & Process',
          guidance: 'Copyright is automatic on creation. Start with KDP\'s free ISBN. Expand to IngramSpark when you\'re ready for wider distribution.',
        },
        'Timing — I don\'t know when my book is ready to publish': {
          label: 'Timing',
          guidance: 'The book is ready when it\'s edited, proofread, and formatted — and when you can read it without finding something you know how to fix. Done is better than perfect.',
        },
      };

      const PRIORITY_SHORT = {
        'Creative control — I want full say over cover, pricing, content': 'Creative Control → Indie publishing',
        'Speed — I want to get this out as fast as reasonably possible': 'Speed → Indie publishing',
        'Credibility — I want the backing of a publisher name': 'Credibility → Traditional or Hybrid',
        'Revenue — I want the highest return per sale': 'Revenue → Indie (70% royalty)',
        'Reach — I want to get into as many hands as possible': 'Reach → Wide distribution (Draft2Digital + IngramSpark)',
      };

      checkPageBreak(14);
      y += 4;

      // Section header
      doc.setFillColor(45, 80, 22);
      doc.rect(20, y, 175, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text('YOUR PUBLISHING READINESS PROFILE', 23, y + 5.5);
      y += 14;

      // Helper: amber sub-heading
      const drawCaretakerHeading = (text) => {
        checkPageBreak(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(232, 119, 34);
        doc.setFontSize(10);
        doc.text(text, 20, y);
        y += 6;
      };

      // Helper: label + value row
      const drawCaretakerRow = (label, value) => {
        if (!value || !String(value).trim()) return;
        const labelW = 48;
        const valueLines = doc.splitTextToSize(String(value), 175 - labelW - 4);
        const rowH = Math.max(6, valueLines.length * 5 + 2);
        checkPageBreak(rowH + 3);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 87, 171);
        doc.setFontSize(9);
        doc.text(label, 20, y + 4);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.text(valueLines, 20 + labelW, y + 4);
        y += rowH;
      };

      // Knowledge level
      drawCaretakerHeading('Publishing Knowledge Level');
      if (d3.caretaker_knowledge_level) {
        const shortLabel = d3.caretaker_knowledge_level.split(' — ')[0];
        drawCaretakerRow('Your Level:', shortLabel);
      }
      y += 4;

      // Publishing fear + guidance box
      drawCaretakerHeading('Your Publishing Fear');
      const fearData = FEAR_LABELS[d3.caretaker_publish_fear];
      if (fearData) {
        drawCaretakerRow('Fear Area:', fearData.label);

        const guidanceLines = doc.splitTextToSize(fearData.guidance, 163);
        const boxH = guidanceLines.length * 5 + 10;
        checkPageBreak(boxH + 4);

        doc.setFillColor(254, 243, 232);
        doc.setDrawColor(232, 119, 34);
        doc.setLineWidth(0.5);
        doc.roundedRect(20, y, 175, boxH, 2, 2, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 80, 22);
        doc.setFontSize(9);
        doc.text('A word for you:', 24, y + 5.5);

        doc.setFont('helvetica', 'italic');
        doc.setTextColor(50, 50, 50);
        doc.text(guidanceLines, 24, y + 10.5);
        y += boxH + 6;
      } else if (d3.caretaker_publish_fear) {
        drawCaretakerRow('Fear Area:', d3.caretaker_publish_fear);
        y += 4;
      }

      // Publishing priority
      drawCaretakerHeading('Your Publishing Priority');
      const priorityShort = PRIORITY_SHORT[d3.caretaker_publish_priority];
      drawCaretakerRow('Priority:', priorityShort || d3.caretaker_publish_priority);
      y += 4;

      // Platform readiness
      drawCaretakerHeading('Platform Readiness');
      if (d3.caretaker_biz_readiness) {
        const platformShort = d3.caretaker_biz_readiness.split(' — ')[0].replace('I have ', '').replace('I haven\'t ', 'Haven\'t ');
        drawCaretakerRow('Current Status:', platformShort);
      }
      y += 4;

      // Closing divider
      doc.setDrawColor(232, 119, 34);
      doc.line(20, y, 195, y);
      y += 10;
    }
    // END CARETAKER PUBLISHING READINESS INJECTION


    addSection("THE CONNECTOR — Marketing Clarity", sentences.ce4, "Selling is serving. The most unloving thing is hiding your book from the person who needs it.");

    // MARKETING PLAN INJECTION
    if (formData[4]?.mkt_survey_complete) {
      const d4 = formData[4];

      checkPageBreak(14);
      y += 4;

      // ── Section header ───────────────────────────────────────────────────
      doc.setFillColor(45, 80, 22);
      doc.rect(20, y, 175, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text("GOSPELWISE AUTHOR MARKETING PLAN", 23, y + 5.5);
      y += 14;

      // ── Local helpers ────────────────────────────────────────────────────

      // Draw an amber sub-heading label
      const drawMktHeading = (text) => {
        checkPageBreak(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(232, 119, 34);
        doc.setFontSize(10);
        doc.text(text, 20, y);
        y += 6;
      };

      // Draw a label + value row — skips row if value is empty
      const drawMktRow = (label, value) => {
        if (!value || !String(value).trim()) return;
        const safeVal = String(value);
        const labelW = 50;
        const valueLines = doc.splitTextToSize(safeVal, 175 - labelW - 4);
        const rowH = Math.max(6, valueLines.length * 5 + 2);
        checkPageBreak(rowH + 3);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 87, 171);
        doc.setFontSize(9);
        doc.text(label, 20, y + 4);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.text(valueLines, 20 + labelW, y + 4);

        y += rowH;
      };

      // ── 1. Your Book ─────────────────────────────────────────────────────
      drawMktHeading('Your Book');
      drawMktRow('Core Message:',   d4.mkt_core_message);
      drawMktRow('Target Reader:',  d4.mkt_target_reader);
      drawMktRow('Transformation:', d4.mkt_transformation);
      y += 4;

      // ── 2. Launch Goals ──────────────────────────────────────────────────
      drawMktHeading('Launch Goals');
      drawMktRow('Primary Goal:',   d4.mkt_primary_goal);
      drawMktRow('Success Metric:', d4.mkt_success_metric);
      drawMktRow('Timeline:',       d4.mkt_timeline);
      y += 4;

      // ── 3. Marketing Strategy ────────────────────────────────────────────
      drawMktHeading('Marketing Strategy');
      drawMktRow('Content Style:',    d4.mkt_content_style);
      drawMktRow('Author Strengths:', d4.mkt_strengths);

      const channelDisplay = Array.isArray(d4.mkt_channels) && d4.mkt_channels.length > 0
        ? d4.mkt_channels.join(', ')
        : null;
      drawMktRow('Channels (1–3):', channelDisplay);
      drawMktRow('Ad Platform:',    d4.mkt_ad_platform);
      y += 4;

      // ── 4. First Action ──────────────────────────────────────────────────
      drawMktHeading('First Action (15 min)');

      if (d4.mkt_first_action && String(d4.mkt_first_action).trim()) {
        const actionLines = doc.splitTextToSize(String(d4.mkt_first_action), 163);
        const boxH = actionLines.length * 5 + 10;
        checkPageBreak(boxH + 4);

        doc.setFillColor(254, 243, 232);
        doc.setDrawColor(232, 119, 34);
        doc.setLineWidth(0.5);
        doc.roundedRect(20, y, 175, boxH, 2, 2, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 80, 22);
        doc.setFontSize(9);
        doc.text('Do this today:', 24, y + 5.5);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.text(actionLines, 24, y + 10.5);

        y += boxH + 6;
      }

      // ── Closing divider ──────────────────────────────────────────────────
      doc.setDrawColor(232, 119, 34);
      doc.line(20, y, 195, y);
      y += 10;
    }

// END MARKETING PLAN INJECTION

    // NEXT FAITHFUL STEP
    checkPageBreak(50);

    doc.setFillColor(45, 80, 22);
    doc.rect(20, y, 175, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text("YOUR ONE NEXT FAITHFUL STEP", 23, y + 5.5);

    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(12);
    const finalLines = doc.splitTextToSize(d5.one_thing || '[Not completed]', 170);
    doc.text(finalLines, 20, y);
    y += (finalLines.length * 5) + 5;

    doc.setFont('helvetica', 'bold');
    doc.text(`By: ${d5.final_date || '[Not completed]'}`, 20, y);
    y += 6;
    doc.text(`Accountability: ${d5.final_accountability || '[Not completed]'}`, 20, y);
   y += 10;

    checkPageBreak(25);

    doc.setFillColor(237, 224, 204);
    doc.rect(20, y, 175, 15, 'F');
    doc.setDrawColor(45, 80, 22);
    doc.setLineWidth(1);
    doc.line(20, y, 20, y + 15);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(45, 80, 22);
    doc.setFontSize(10);
    doc.text("Available and faithful. That's the whole assignment. All for God's glory and your neighbor's gospel good.", 105, y + 6, { align: 'center' });
    doc.text("— Jeremy Bouma, GospelWise Author", 105, y + 11, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text("© 2026 CROSS + PEN · gospelwiseauthor.com · Wield the pen for the sake of the cross.", 105, 288, { align: 'center' });

    doc.save('GospelWise_Author_Compass.pdf');
  };

  const generateWorkbookPDF = () => {
    if (!isAAP) return;
    const doc = new jsPDF();
    let y = 20;

    const checkPage = (heightNeeded) => {
      if (y + heightNeeded > 270) {
        doc.addPage();
        y = 20;
      }
    };

    const addHeader = (title) => {
      checkPage(15);
      doc.setFillColor(45, 80, 22);
      doc.rect(20, y, 175, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.text(title, 25, y + 7);
      y += 18;
    };

    const addQnA = (q, a) => {
      checkPage(25);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 87, 171);
      doc.setFontSize(10);
      const qLines = doc.splitTextToSize(q, 170);
      doc.text(qLines, 20, y);
      y += (qLines.length * 4) + 2;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);
      const aLines = doc.splitTextToSize(a || '[Not completed]', 165);
      doc.text(aLines, 25, y);
      y += (aLines.length * 5) + 8;
    };

    // PDF Main Header
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(45, 80, 22);
    doc.setFontSize(20);
    doc.text("GospelWise Author Workbook", 105, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Prepared by ${user.name}`, 105, y, { align: 'center' });
    y += 15;

    // DAY 1
    addHeader("DAY 1: THE CHRISTIAN");
    addQnA("What book has God placed on your heart?", formData[1]?.book_heart);
    addQnA("Why did this book land on you?", formData[1]?.why_me);
    addQnA("What is the vocational why underneath this book?", formData[1]?.vocational_why);
    addQnA("Is there a Scripture that anchors your writing vocation?", formData[1]?.scripture_anchor);
    addQnA("What is the one thought, fear, or circumstance most likely to stop you?", formData[1]?.stopping_me);
    addQnA("What does faithfulness look like for your writing this season?", formData[1]?.faithfulness);
    addQnA("Write the one mindset reframe you most need to carry with you this week.", formData[1]?.mindset_reframe);

    // Stewardship Profile PDF Export
    if (formData[1]?.survey_completed) {
      checkPage(80);
      addHeader("STEWARDSHIP PROFILE RESULTS");
      
      const d1 = formData[1];
      const strongKey = d1.survey_strongest || 'christian';
      const weakKey = d1.survey_weakest || 'christian';

      // Draw bars
      const scores = [
        { label: 'The Christian', score: d1.survey_christian_score || 0 },
        { label: 'The Creator', score: d1.survey_creator_score || 0 },
        { label: 'The Caretaker', score: d1.survey_caretaker_score || 0 },
        { label: 'The Connector', score: d1.survey_connector_score || 0 }
      ];

      scores.forEach(s => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(10);
        doc.text(`${s.label} (${s.score}/12)`, 20, y);
        y += 4;
        
        // Background bar
        doc.setFillColor(230, 230, 230);
        doc.rect(20, y, 100, 5, 'F');
        
        // Filled bar
        doc.setFillColor(37, 87, 171); // Blue
        if (s.label.toLowerCase().includes(weakKey)) {
          doc.setFillColor(232, 119, 34); // Orange for weakest
        }
        doc.rect(20, y, (s.score / 12) * 100, 5, 'F');
        y += 10;
      });
      y += 5;

      if (SURVEY_RESULTS[strongKey]) {
        addQnA("Strongest Area: " + SURVEY_RESULTS[strongKey].name, SURVEY_RESULTS[strongKey].strong);
      }
      if (SURVEY_RESULTS[weakKey]) {
        addQnA("Growth Opportunity: " + SURVEY_RESULTS[weakKey].name, SURVEY_RESULTS[weakKey].weak);
        addQnA("Recommended Next Step", "Check out: " + SURVEY_RESULTS[weakKey].link);
      }
    }

    // DAY 2
    addHeader("DAY 2: THE CREATOR");
    addQnA("What stage is your manuscript at right now?", formData[2]?.stage);
    addQnA("Describe where your project is right now.", formData[2]?.project_desc);
    addQnA("What is your realistic manuscript completion date?", formData[2]?.completion_date);
    addQnA("What is the single biggest thing stopping you?", formData[2]?.biggest_hurdle);
    addQnA("How much time can you realistically give to writing each week?", formData[2]?.time_per_week);
    addQnA("What does your most productive writing session look like?", formData[2]?.productive_session);

    // DAY 3
    addHeader("DAY 3: THE CARETAKER");
    addQnA("Which publishing path are you leaning toward — and why?", formData[3]?.path);
    addQnA("What do you still need to learn or decide before you can publish?", formData[3]?.publishing_gaps);
    addQnA("Which formats will you publish in?", (formData[3]?.formats || []).join(', '));
    addQnA("Distribution platform:", formData[3]?.platform);
    addQnA("ISBN plan:", formData[3]?.isbn_plan);
    addQnA("Cover design plan:", formData[3]?.cover_plan);
    addQnA("What feels right for pricing your book and why?", formData[3]?.pricing);

    // DAY 4
    addHeader("DAY 4: THE CONNECTOR");
    addQnA("Give your reader a name:", formData[4]?.reader_name);
    addQnA("Describe their life in 2–3 sentences:", formData[4]?.reader_life);
    addQnA("What are they struggling with that your book speaks directly into?", formData[4]?.reader_struggle);
    addQnA("What do you hope God does in their life through your book?", formData[4]?.reader_hope);
    addQnA("Website status:", formData[4]?.website_status);
    addQnA("Email list status:", formData[4]?.email_status);
    addQnA("Social media presence:", formData[4]?.social_status);
    addQnA("Which minimum do you need to build or strengthen first?", formData[4]?.marketing_first_minimum);
    addQnA("What is one piece of content you could create this week?", formData[4]?.marketing_first_content);

    // DAY 5
    addHeader("DAY 5: YOUR COMPASS SUMMARY");
    const s = getCompassSentences();
    addQnA("Calling Clarity:", s.ce1);
    addQnA("Writing Clarity:", s.ce2);
    addQnA("Publishing Clarity:", s.ce3);
    addQnA("Marketing Clarity:", s.ce4);
    addQnA("Your One Next Faithful Step:", formData[5]?.one_thing);
    addQnA("By what specific date will you take this step?", formData[5]?.final_date);
    addQnA("Accountability:", formData[5]?.final_accountability);
    addQnA("What would it mean if you actually finish this book?", formData[5]?.final_meaning);

    doc.save('GospelWise_Author_Workbook.pdf');
  };

  const renderTextArea = (day, field, label, rows = 4) => (
    <div className="mb-6">
      <label className="block font-bold mb-2 text-[#2557AB]">{label}</label>
      <textarea
        value={formData[day]?.[field] || ''}
        onChange={e => handleChange(day, field, e.target.value)}
        onBlur={() => handleSave(day)}
        className="w-full border-2 border-[#E87722] bg-[#FDF6EC] rounded p-3 font-poppins focus:outline-none focus:ring-2 focus:ring-[#2D5016]"
        rows={rows}
      />
    </div>
  );

  const renderInput = (day, field, label, placeholder = '') => (
    <div className="mb-6">
      <label className="block font-bold mb-2 text-[#2557AB]">{label}</label>
      <input
        type="text"
        value={formData[day]?.[field] || ''}
        onChange={e => handleChange(day, field, e.target.value)}
        onBlur={() => handleSave(day)}
        placeholder={placeholder}
        className="w-full border-2 border-[#E87722] bg-[#FDF6EC] rounded p-3 font-poppins focus:outline-none focus:ring-2 focus:ring-[#2D5016]"
      />
    </div>
  );

  const renderRadio = (day, field, label, options) => (
    <div className="mb-6">
      <label className="block font-bold mb-2 text-[#2557AB]">{label}</label>
      <div className="space-y-2">
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-3 p-3 border border-[#EDE0CC] rounded-md hover:bg-[#FDF6EC] cursor-pointer">
            <input type="radio"
              name={field}
              value={opt}
              checked={formData[day]?.[field] === opt}
              onChange={e => { handleChange(day, field, e.target.value); handleSave(day, true); }}
              className="w-4 h-4 text-[#2D5016]"
            />
            <span className="text-gray-800">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderCheckbox = (day, field, label, options) => (
    <div className="mb-6">
      <label className="block font-bold mb-2 text-[#2557AB]">{label}</label>
      <div className="space-y-2">
        {options.map(opt => {
          const isChecked = (formData[day]?.[field] || []).includes(opt);
          return (
            <label key={opt} className="flex items-center gap-3 p-3 border border-[#EDE0CC] rounded-md hover:bg-[#FDF6EC] cursor-pointer">
              <input type="checkbox"
                checked={isChecked}
                onChange={e => {
                  const current = formDataRef.current[day]?.[field] || [];
                  const updated = e.target.checked ? [...current, opt] : current.filter(item => item !== opt);
                  handleChange(day, field, updated);
                  handleSave(day, true);
                }}
                className="w-4 h-4 text-[#2D5016] rounded"
              />
              <span className="text-gray-800">{opt}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  const renderCallout = (text) => (
    <div className="bg-[#EDE0CC] border-l-4 border-[#2D5016] p-4 italic text-gray-800 mb-6">
      {text}
    </div>
  );

  const renderNavLinks = () => (
    <nav className="space-y-2">
      {DAYS.map((d) => {
        const isUnlocked = !isDayLocked(d.day);
        const active = activeDay === d.day;
        const completed = formData[d.day] && Object.keys(formData[d.day]).length > 2; 
        
        return (
          <button
            key={d.day}
            onClick={() => {
              if (isUnlocked) {
                setActiveDay(d.day);
                window.scrollTo({ top: 0, behavior: 'smooth' }); 
              }
            }}
            disabled={!isUnlocked}
            className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
              ${active ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md' : 
                isUnlocked ? 'bg-white border-[#EDE0CC] hover:border-[#E87722] text-[#2D5016]' : 
                'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
              <div className={`font-bold ${active ? 'text-white' : 'text-[#2D5016]'}`}>{d.title}</div>
              <div className={`text-xs ${active ? 'text-[#EDE0CC]' : 'text-gray-500'}`}>{d.subtitle}</div>
            </div>
            <div>
              {!isUnlocked ? (
                <SafeIcon name="Lock" className="text-gray-400" />
              ) : completed && !active ? (
                <SafeIcon name="CheckCircle" className="text-[#2D5016]" />
              ) : active ? (
                <div className="w-3 h-3 rounded-full bg-[#E87722]"></div>
              ) : null}
            </div>
          </button>
        );
      })}
    </nav>
  );

  // --- SURVEY RENDERERS ---
  const renderSurveyResults = () => {
    const d = formData[1] || {};
    const strongKey = d.survey_strongest || 'christian';
    const weakKey = d.survey_weakest || 'christian';
    
    const scores = [
      { key: 'christian', label: 'The Christian', score: d.survey_christian_score || 0 },
      { key: 'creator', label: 'The Creator', score: d.survey_creator_score || 0 },
      { key: 'caretaker', label: 'The Caretaker', score: d.survey_caretaker_score || 0 },
      { key: 'connector', label: 'The Connector', score: d.survey_connector_score || 0 },
    ];

    return (
      <div className="bg-white p-6 md:p-8 rounded-lg border-2 border-[#2D5016] shadow-lg mt-8 text-left">
        <h3 className="text-2xl font-bold text-center text-[#2D5016] mb-8">
          Here's your GospelWise Author Stewardship Profile, {user?.name ? user.name.split(' ')[0] : 'Author'}
        </h3>
        
        <div className="space-y-5 mb-10 max-w-2xl mx-auto">
          {scores.map(s => (
            <div key={s.key}>
              <div className="flex justify-between text-sm font-bold mb-1 text-gray-800">
                <span>{s.label}</span>
                <span>{s.score} / 12</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden border border-gray-300">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${s.key === weakKey ? 'bg-[#E87722]' : 'bg-[#2557AB]'}`} 
                  style={{ width: `${(s.score / 12) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#EBF2E3] p-6 rounded-lg border border-[#2D5016]">
            <h4 className="font-bold text-[#2D5016] text-lg mb-3">Your strongest Persona: {SURVEY_RESULTS[strongKey]?.name}</h4>
            <p className="text-gray-800 leading-relaxed">{SURVEY_RESULTS[strongKey]?.strong}</p>
          </div>
          <div className="bg-[#FDF6EC] p-6 rounded-lg border border-[#E87722]">
            <h4 className="font-bold text-[#E87722] text-lg mb-3">Your biggest stewardship gap: {SURVEY_RESULTS[weakKey]?.name}</h4>
            <p className="text-gray-800 leading-relaxed">{SURVEY_RESULTS[weakKey]?.weak}</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-8 rounded-lg text-center shadow-sm">
          <h4 className="font-bold text-[#2557AB] text-xl mb-3">Recommended Resource</h4>
          <p className="text-gray-700 mb-6 text-lg">Based on your profile, we recommend diving into this next step:</p>
          <a href={SURVEY_RESULTS[weakKey]?.url} className="inline-block bg-[#2557AB] text-white font-bold py-3 px-8 rounded hover:bg-blue-800 transition-colors shadow-md">
            {SURVEY_RESULTS[weakKey]?.link}
          </a>
        </div>

        <div className="mt-8 text-center pb-4">
          <button onClick={() => { setRetakeSurvey(true); setSurveyStep(1); }} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">
            Retake the Stewardship Audit
          </button>
        </div>
      </div>
    );
  };

  const renderSurveyQuestionStep = () => {
    const stepData = STEWARDSHIP_SURVEY[surveyStep - 1];
    const canProceed = stepData.qs.every(q => formData[1]?.[q.id]);

    return (
      <div className="bg-white p-6 md:p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 text-left">
        <div className="text-sm font-bold text-[#E87722] mb-2 uppercase tracking-wider">
          Section {surveyStep} of 4 · {stepData.label.split('·')[0]}
        </div>
        <h3 className="text-2xl font-bold text-[#2D5016] mb-2">{stepData.label}</h3>
        <p className="text-gray-600 mb-8 text-lg">{stepData.desc}</p>

        <div className="space-y-8">
          {stepData.qs.map((q, idx) => (
            <div key={q.id} className="bg-gray-50 p-6 rounded-lg border border-gray-100">
              <p className="font-bold text-gray-800 mb-4 text-lg">
                <span className="text-[#2D5016] mr-2">Q{((surveyStep-1)*3) + idx + 1}.</span> 
                {q.text}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { val: 1, label: 'Never' },
                  { val: 2, label: 'Sometimes' },
                  { val: 3, label: 'Often' },
                  { val: 4, label: 'Consistently' }
                ].map(opt => (
                  <label key={opt.val} className={`cursor-pointer border-2 rounded-md p-3 text-center transition-all ${formData[1]?.[q.id] === opt.val ? 'bg-[#2D5016] text-white border-[#2D5016] shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-[#2D5016]'}`}>
                    <input 
                      type="radio" 
                      name={q.id} 
                      value={opt.val} 
                      checked={formData[1]?.[q.id] === opt.val}
                      onChange={() => {
                        handleChange(1, q.id, opt.val);
                        handleSave(1, true);
                      }}
                      className="hidden" 
                    />
                    <span className="block font-bold text-lg">{opt.val}</span>
                    <span className="block text-xs mt-1 opacity-80 uppercase tracking-widest">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200">
          {surveyStep > 1 ? (
            <button 
              onClick={() => { setSurveyStep(s => s - 1); }}
              className="text-[#2D5016] font-bold hover:underline px-4 py-2"
            >
              &larr; Previous
            </button>
          ) : <div></div>}
          
          {surveyStep < 4 ? (
            <Button 
              onClick={() => { setSurveyStep(s => s + 1); }}
              disabled={!canProceed}
              className={!canProceed ? 'opacity-50 cursor-not-allowed' : 'bg-[#2D5016] text-white'}
            >
              Next Section
            </Button>
          ) : (
            <Button 
              onClick={() => calculateSurveyResults()}
              disabled={!canProceed}
              className={!canProceed ? 'opacity-50 cursor-not-allowed' : 'bg-[#E87722] text-white font-bold'}
            >
              View My Results
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderSurveySection = () => {
    const isSurveyCompleted = formData[1]?.survey_completed;
    const currentStep = (isSurveyCompleted && !retakeSurvey) ? 5 : surveyStep;

    return (
      <section className="mt-12 border-t-2 border-dashed border-[#EDE0CC] pt-12">
        <div className="text-center mb-8">
          <h3 className="text-[#2D5016] font-bold text-3xl mb-3">GospelWise Author Stewardship Profile</h3>
          <p className="text-gray-700 text-lg max-w-2xl mx-auto">Take this 12-question audit to discover your strongest persona and your biggest stewardship gap. Be honest — your results will guide your next best step.</p>
        </div>
        
        {currentStep === 5 ? renderSurveyResults() : renderSurveyQuestionStep()}
      </section>
    );
  };

  // --- DAY 1 RENDER ---
  const renderDay1 = () => (
    <div className="bg-[#FDF6EC] rounded-lg font-poppins shadow-sm border border-[#EDE0CC]">
      <div className="bg-[#2D5016] text-white font-bold px-6 py-3 rounded-t-lg tracking-widest uppercase">
        DAY 1 — THE CHRISTIAN · CALLING
      </div>
      <div className="p-6 md:p-8 bg-white rounded-b-lg space-y-8">
        <p className="text-gray-700 leading-relaxed text-lg">Before you are a writer, you are a Christian. Before you are an author, you are someone who has been created in Christ to create — gifted by God to wield the pen for the sake of the cross. Everything else in your author journey flows from this. This section gets at the heart of your why. Not why you want to be published. Why this book. Why now. Why you.</p>
        
        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Calling</h3>
          {renderTextArea(1, 'book_heart', 'What book has God placed on your heart? (Working title or description is fine.)')}
          {renderTextArea(1, 'why_me', 'In one or two sentences, why did this book land on you — not someone else?')}
          {renderCallout("Example: 'I've spent twenty years watching women in my church feel invisible. I want to write a novel where a woman like them is the hero of a story that points to Christ.'")}
          {renderTextArea(1, 'vocational_why', 'What is the vocational why underneath this book? Why does writing matter to you as a Christian — not just why this book, but why the calling itself?', 5)}
          {renderInput(1, 'scripture_anchor', 'Is there a Scripture that anchors your writing vocation? Write it here. (Optional but powerful.)')}
          {renderCallout("Jeremy's anchor: Acts 20:24 — 'I consider my life worth nothing to me. My only aim is to finish the race and complete the task the Lord Jesus has given me — the task of testifying to the good news of God's grace.' Find yours.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Obstacle</h3>
          {renderTextArea(1, 'stopping_me', 'What is the one thought, fear, or circumstance most likely to stop you from finishing this book?')}
          {renderTextArea(1, 'faithfulness', 'What does faithfulness look like for your writing this season — not perfection, just faithfulness?')}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Mindset Shift</h3>
          {renderTextArea(1, 'mindset_reframe', 'Based on today\'s Morning Assembly, write the one mindset reframe you most need to carry with you this week.')}
          {renderCallout("'Available and faithful.' That's what God asks of you. Not brilliant. Not fast. Not published by Tuesday. Available. And faithful.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Day 1 Compass Entry</h3>
          <div className="bg-[#FDF6EC] border-t-4 border-[#E87722] p-6 rounded shadow-inner">
            <p className="text-gray-800 mb-4 font-medium">Complete this statement — this is the calling clarity you'll carry through the week:</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">I am writing...</label>
                <input type="text" value={formData[1]?.ce_book || ''} onChange={e => handleChange(1, 'ce_book', e.target.value)} onBlur={() => handleSave(1)} placeholder="your book title or description" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">...for...</label>
                <input type="text" value={formData[1]?.ce_person || ''} onChange={e => handleChange(1, 'ce_person', e.target.value)} onBlur={() => handleSave(1)} placeholder="one specific person" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">...because...</label>
                <textarea value={formData[1]?.ce_why || ''} onChange={e => handleChange(1, 'ce_why', e.target.value)} onBlur={() => handleSave(1)} placeholder="why this matters to God and to them" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" rows={2} />
              </div>
            </div>
            <div className="p-4 bg-white border border-[#EDE0CC] rounded text-center shadow-sm">
              <p className="text-lg italic text-gray-800 font-medium">{getCompassSentences().ce1}</p>
            </div>
          </div>
        </section>

        {renderSurveySection()}

        <div className="flex items-center gap-2 text-[#2D5016] font-bold border-t border-[#EDE0CC] pt-6 mt-8">
          <SafeIcon name="CheckSquare" /> Day 1 Cabin Call complete. Share your one-sentence why in tonight's Evening Campfire.
        </div>
      </div>
    </div>
  );

  // --- DAY 2 RENDER ---
  const renderDay2 = () => (
    <div className="bg-[#FDF6EC] rounded-lg font-poppins shadow-sm border border-[#EDE0CC]">
      <div className="bg-[#2D5016] text-white font-bold px-6 py-3 rounded-t-lg tracking-widest uppercase">
        DAY 2 — THE CREATOR · WRITING
      </div>
      <div className="p-6 md:p-8 bg-white rounded-b-lg space-y-8">
        <p className="text-gray-700 leading-relaxed text-lg">Writers write. That sounds obvious — but for most Christian authors the hardest part of the creator role isn't craft. It's consistency. Showing up when the chapter won't cooperate, when life interrupts, when the calling feels distant. This section gets honest about where your manuscript actually is, what's been stopping it, and what a faithful writing rhythm looks like for your real life.</p>
        
        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Manuscript Stage</h3>
          {renderRadio(2, 'stage', 'What stage is your manuscript at right now? Select one:', ['Idea / Outline Stage', 'Draft in Progress', 'Rough Complete', 'Ready to Refine'])}
          {renderTextArea(2, 'project_desc', 'In a few sentences, describe where your project is right now. Be specific and honest.', 5)}
          {renderInput(2, 'completion_date', 'What is your realistic manuscript completion date? Name a specific date — not a season.', 'e.g. September 15, 2026')}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Sticking Point</h3>
          {renderTextArea(2, 'biggest_hurdle', 'What is the single biggest thing stopping you from moving your manuscript forward right now?')}
          {renderCallout("The two most common sticking points for Christian authors: 'It has to be right before I can keep going.' (Perfectionism) and 'I'm not sure if it's any good.' (Isolation). Both are solvable. Neither is the truth about your work.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Writing Rhythm</h3>
          {renderTextArea(2, 'time_per_week', 'How much time can you realistically give to writing each week — right now, with your actual life?', 3)}
          {renderTextArea(2, 'productive_session', 'What does your most productive writing session look like? Time of day, location, length, conditions.')}
          {renderCallout("Writers write. Finish the draft before you revise it. Your first job is completion, not perfection. The rock tumbler smooths out the very thing that makes your voice worth reading — don't tumble it before it's done.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Day 2 Compass Entry</h3>
          <div className="bg-[#FDF6EC] border-t-4 border-[#E87722] p-6 rounded shadow-inner">
            <p className="text-gray-800 mb-4 font-medium">Complete this statement:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">My manuscript is...</label>
                <input type="text" value={formData[2]?.ce_stage || ''} onChange={e => handleChange(2, 'ce_stage', e.target.value)} onBlur={() => handleSave(2)} className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">My one writing move this week is...</label>
                <input type="text" value={formData[2]?.ce_action || ''} onChange={e => handleChange(2, 'ce_action', e.target.value)} onBlur={() => handleSave(2)} className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">I will write/edit...</label>
                <input type="text" value={formData[2]?.ce_amount || ''} onChange={e => handleChange(2, 'ce_amount', e.target.value)} onBlur={() => handleSave(2)} placeholder="e.g. 340 pages / 3 chapters" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">...by...</label>
                <input type="text" value={formData[2]?.ce_date || ''} onChange={e => handleChange(2, 'ce_date', e.target.value)} onBlur={() => handleSave(2)} placeholder="e.g. July 20" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
            </div>
            <div className="p-4 bg-white border border-[#EDE0CC] rounded text-center shadow-sm">
              <p className="text-lg italic text-gray-800 font-medium">{getCompassSentences().ce2}</p>
            </div>
          </div>
        </section>

{(() => {
  const step = creatorSurveyStep;

  const CREATOR_BLOCK_GUIDANCE = {
    'Perfectionism — I can\'t move forward until it\'s right': {
      label: 'Perfectionism',
      guidance: 'The first draft\'s only job is to exist. Heinlein\'s Rule: finish what you write — don\'t rewrite until there\'s something to rewrite. Give yourself permission to write it wrong first.',
    },
    'Isolation — I don\'t know if it\'s any good': {
      label: 'Isolation',
      guidance: 'Your voice is what your reader came for. They\'re not coming for a perfect book — they\'re coming for your perspective. Find one trusted reader, not five. Community over committee.',
    },
    'Time — life keeps getting in the way': {
      label: 'Time',
      guidance: 'Writers who finish don\'t have more time — they protect what they have. A sustainable rhythm beats an ambitious one. Even 20 minutes a day is 120 hours a year.',
    },
    'Fear — what if no one reads it / what if they do': {
      label: 'Fear',
      guidance: 'Both fears are real and both are conquerable. The calling is bigger than the fear. Acts 20:24: finish the task the Lord Jesus has given you. The rest is his.',
    },
    'Clarity — I\'m not sure what I\'m trying to say': {
      label: 'Clarity',
      guidance: 'Write to find out what you think. The first draft is a discovery draft — clarity comes through writing, not before it. Start with the one thing you most want your reader to feel or know.',
    },
    'Comparison — I feel behind other writers': {
      label: 'Comparison',
      guidance: 'You\'re not in a race. The only manuscript that matters is yours. Theodore Roosevelt was right: comparison is the thief of joy — and of creative output. Write your book, not theirs.',
    },
  };

  const CREATOR_STYLE_LABELS = {
    'Planner (outline everything before I write)': 'Plotter',
    'Pantser (discover the story as I write)': 'Pantser',
    'Hybrid (loose outline, fill it in as I go)': 'Hybrid',
    'Not sure yet': 'Still discovering',
  };

  if (step === 0) {
    if (formData[2]?.creator_survey_complete) {
      setCreatorSurveyStep(5);
      return null;
    }
    return (
      <div className="bg-white p-8 rounded-lg border-2 border-[#2D5016] shadow-lg mt-8 mb-6 text-center">
        <h3 className="text-2xl font-bold text-[#2D5016] mb-2 uppercase tracking-tight">
          Your Writing Identity Profile
        </h3>
        <p className="text-[#E87722] font-bold text-lg mb-4">
          Discover how you write — and what's actually stopping you.
        </p>
        <p className="text-gray-700 mb-6 max-w-xl mx-auto leading-relaxed">
          Four quick questions that go deeper than the workbook. Know your writing style,
          name your real block, and find the approach that will actually help you finish.
        </p>
        <Button
          onClick={() => setCreatorSurveyStep(1)}
          className="bg-[#2D5016] text-white font-bold py-3 px-8 rounded text-lg shadow-md hover:bg-green-800 transition-all inline-flex items-center gap-2 mx-auto"
        >
          Start the Profile <SafeIcon name="ArrowRight" />
        </Button>
      </div>
    );
  }

  const renderOptionButtons = (day, field, options, cols = 1) => (
    <div className={`grid grid-cols-1 ${cols === 2 ? 'md:grid-cols-2' : ''} gap-3 mt-4`}>
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => {
            handleChange(day, field, opt);
            handleSave(day, true);
          }}
          className={`p-3 text-left rounded-md border-2 transition-all font-medium text-sm
            ${formData[day]?.[field] === opt
              ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md'
              : 'bg-white border-gray-200 text-gray-700 hover:border-[#2D5016] hover:bg-[#EBF2E3]'
            }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const StepHeader = ({ stepNum, title }) => (
    <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
      <div>
        <div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">
          Step {stepNum} of 4
        </div>
        <h3 className="text-2xl font-bold text-[#2D5016]">{title}</h3>
      </div>
      <button
        onClick={() => setCreatorSurveyStep(0)}
        className="text-sm text-gray-500 hover:text-gray-800 underline font-medium"
      >
        Exit Profile
      </button>
    </div>
  );

  if (step === 1) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={1} title="Your Writing Style" />
        <p className="text-gray-600 mb-8 text-lg">
          How you write shapes everything about how you should approach finishing your manuscript.
        </p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">
              Which best describes how you write?
            </label>
            {renderOptionButtons(2, 'creator_writing_style', [
              'Planner (outline everything before I write)',
              'Pantser (discover the story as I write)',
              'Hybrid (loose outline, fill it in as I go)',
              'Not sure yet',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2">
            <span>📖</span> From Jeremy's Vault
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            "I'm not the biggest fan of the formula language. Think of it as a foundational code,
            installed in the back of your creative brain. Whether you're a plotter or pantser —
            the story finds its way when you show up and write it."
          </p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCreatorSurveyStep(0)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCreatorSurveyStep(2)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={2} title="Your Writing Block" />
        <p className="text-gray-600 mb-8 text-lg">
          Naming your block is the first step to getting past it. Be honest — this is just for you.
        </p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">
              What's really stopping you from moving your manuscript forward?
            </label>
            {renderOptionButtons(2, 'creator_block_type', [
              'Perfectionism — I can\'t move forward until it\'s right',
              'Isolation — I don\'t know if it\'s any good',
              'Time — life keeps getting in the way',
              'Fear — what if no one reads it / what if they do',
              'Clarity — I\'m not sure what I\'m trying to say',
              'Comparison — I feel behind other writers',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>✦</span> Remember</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            Every writer has a block. The ones who finish are the ones who name it, understand it,
            and build a practice that gets them to the page anyway. "Available and faithful" —
            that's the whole assignment.
          </p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCreatorSurveyStep(1)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCreatorSurveyStep(3)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={3} title="Your Completion Style" />
        <p className="text-gray-600 mb-8 text-lg">
          Writers who finish don't all do it the same way. What actually gets you across the finish line?
        </p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">
              How do you best finish things?
            </label>
            {renderOptionButtons(2, 'creator_completion_style', [
              'Deadlines — I need a date to work toward',
              'Accountability — I need someone checking in on me',
              'Milestones — I need to see progress in chunks',
              'Inspiration — I need the right conditions to show up',
              'Discipline — I just need to sit down and do it',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>📝</span> From Jeremy's Experience</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            "One year I spent 52 weeks writing a short story a week — a writing challenge I paid $600
            to enter. Best thing I ever did for my writing career. Not because of the stories.
            Because it made me finish, week after week. Find the system that makes you a finisher."
          </p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCreatorSurveyStep(2)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCreatorSurveyStep(4)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={4} title="Your Voice Strength" />
        <p className="text-gray-600 mb-8 text-lg">
          Your readers don't just come for the topic — they come for you. What is the thing they'll come back for?
        </p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">
              What is your strongest creative gift as a writer?
            </label>
            {renderOptionButtons(2, 'creator_voice_strength', [
              'Storytelling — my plots and narratives pull people in',
              'Voice — how I say things, my unique perspective',
              'Teaching — I explain things clearly and helpfully',
              'Encouragement — I help people feel less alone',
              'Theology — I help people understand God and Scripture better',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>✦</span> Trust Your Voice</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            "Readers come to our particular book for us — they're reading for our perspective,
            our voice, our experiences. Don't lose it. Defend it. Trust yourself:
            you know what you're doing." — Jeremy Bouma
          </p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCreatorSurveyStep(3)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button
            onClick={() => {
              handleChange(2, 'creator_survey_complete', true);
              handleSave(2, true);
              setCreatorSurveyStep(5);
            }}
            className="bg-[#E87722] text-white px-8 font-bold hover:bg-[#d06b1a]"
          >
            See My Writing Profile &rarr;
          </Button>
        </div>
      </div>
    );
  }

  if (step === 5) {
    const d2 = formData[2] || {};
    const blockData = CREATOR_BLOCK_GUIDANCE[d2.creator_block_type] || null;
    const styleLabel = CREATOR_STYLE_LABELS[d2.creator_writing_style] || d2.creator_writing_style || 'Not answered';

    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
          <div>
            <div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Your Profile</div>
            <h3 className="text-2xl font-bold text-[#2D5016]">Your Writing Identity</h3>
          </div>
          <button onClick={() => setCreatorSurveyStep(1)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Edit Answers</button>
        </div>
        <div className="rounded-lg border border-[#EDE0CC] overflow-hidden mb-6">
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Writing Style</span></div>
          <div className="px-5 py-4 bg-white border-b border-[#EDE0CC]">
            <span className="font-bold text-[#2557AB] text-base">{styleLabel}</span>
            <p className="text-gray-600 text-sm mt-1">
              {d2.creator_writing_style === 'Planner (outline everything before I write)' && 'You work best with structure in place before the words flow. Protect your outline time — it\'s not procrastination, it\'s preparation.'}
              {d2.creator_writing_style === 'Pantser (discover the story as I write)' && 'You discover the story by writing it. Your superpower is organic narrative — trust the process and resist the urge to over-plan.'}
              {d2.creator_writing_style === 'Hybrid (loose outline, fill it in as I go)' && 'You have the best of both approaches. A flexible framework gives you direction without constraining your creativity.'}
              {d2.creator_writing_style === 'Not sure yet' && 'That\'s okay — many writers don\'t know their style until they\'ve finished a draft. Keep writing and your natural approach will emerge.'}
            </p>
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Your Writing Block</span></div>
          <div className="px-5 py-4 bg-[#FDF6EC] border-b border-[#EDE0CC]">
            {blockData ? (
              <>
                <span className="font-bold text-[#E87722] text-base">{blockData.label}</span>
                <p className="text-gray-700 text-sm mt-1 leading-relaxed">{blockData.guidance}</p>
              </>
            ) : (
              <span className="text-gray-400 italic text-sm">Not answered</span>
            )}
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">How You Finish</span></div>
          <div className="px-5 py-4 bg-white border-b border-[#EDE0CC]">
            <span className="font-bold text-[#2557AB] text-base">{d2.creator_completion_style || <span className="text-gray-400 italic font-normal text-sm">Not answered</span>}</span>
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Your Voice Strength</span></div>
          <div className="px-5 py-4 bg-[#FDF6EC]">
            <span className="font-bold text-[#2557AB] text-base">{d2.creator_voice_strength || <span className="text-gray-400 italic font-normal text-sm">Not answered</span>}</span>
          </div>
        </div>
        <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] p-5 rounded mb-6">
          <h4 className="font-bold text-[#2D5016] mb-1 flex items-center gap-2"><span>✦</span> This profile is saved to your Compass.</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            Your writing identity shapes everything — how you structure your time, what kind of
            accountability you seek, and how you defend your voice. Return to it whenever the
            writing gets hard.
          </p>
        </div>
        <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
          <button onClick={() => setCreatorSurveyStep(1)} className="text-sm text-gray-400 hover:text-gray-600 underline">&larr; Edit answers</button>
          <button onClick={() => setCreatorSurveyStep(0)} className="text-sm text-gray-400 hover:text-gray-600 underline">Done</button>
        </div>
      </div>
    );
  }

  return null;
})()}

        <div className="flex items-center gap-2 text-[#2D5016] font-bold border-t border-[#EDE0CC] pt-6">
          <SafeIcon name="CheckSquare" /> Day 2 Cabin Call complete. Share your manuscript stage + one next move in tonight's Evening Campfire.
        </div>
      </div>
    </div>
  );

  // --- DAY 3 RENDER ---
  const renderDay3 = () => (
    <div className="bg-[#FDF6EC] rounded-lg font-poppins shadow-sm border border-[#EDE0CC]">
      <div className="bg-[#2D5016] text-white font-bold px-6 py-3 rounded-t-lg tracking-widest uppercase">
        DAY 3 — THE CARETAKER · PUBLISHING
      </div>
      <div className="p-6 md:p-8 bg-white rounded-b-lg space-y-8">
        <p className="text-gray-700 leading-relaxed text-lg">Once you've written your book, you're no longer just an author — you're a publisher. A caretaker of the work God gave you to steward. This section takes the mystery out of publishing by helping you identify which path fits your book, your calling, and your current season. There is no single right answer. There is only the right answer for you.</p>

        <div className="space-y-4 mb-6">
          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] px-4 py-2 font-bold text-[#2D5016]">Traditional Publishing</div>
            <div className="p-4 text-sm text-gray-700 bg-gray-50">You query agents, an agent shops to publishers, and if acquired the publisher handles editing, design, distribution, and marketing. You receive an advance and royalties. Timeline: 2–4 years. You trade pricing control and most rights for distribution and credibility.</div>
          </div>
          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] px-4 py-2 font-bold text-[#2D5016]">Hybrid Publishing</div>
            <div className="p-4 text-sm text-gray-700 bg-gray-50">A publisher assists with editing, design, and distribution — but you pay upfront costs. You keep more control and higher royalties than traditional. Warning: research carefully. Some hybrid publishers are predatory. Look for full transparency in contracts before signing.</div>
          </div>
          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] px-4 py-2 font-bold text-[#2D5016]">Independent (Indie) Publishing</div>
            <div className="p-4 text-sm text-gray-700 bg-gray-50">You publish directly — Amazon KDP and other platforms. You control everything: cover, pricing, timeline, rights. You pay for editing and design upfront but keep the highest royalty percentage. Timeline: as fast as you move. Pricing is your superpower.</div>
          </div>
        </div>
        
        <section>
          {renderTextArea(3, 'path', 'Which publishing path are you leaning toward — and why?', 5)}
          {renderTextArea(3, 'publishing_gaps', 'What do you still need to learn or decide before you can publish? List up to three things.', 5)}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Publishing Decisions</h3>
          {renderCheckbox(3, 'formats', 'Which formats will you publish in? Check all that apply:', ['Ebook', 'Paperback', 'Hardcover', 'Audiobook'])}
          {renderRadio(3, 'platform', 'If going indie — distribution platform:', ['Amazon KDP exclusive', 'Wide distribution (Draft2Digital + others)', 'Not sure yet'])}
          {renderRadio(3, 'isbn_plan', 'ISBN plan:', ['Use the platform\'s free ISBN', 'Purchase my own ISBN', 'Not sure yet'])}
          {renderRadio(3, 'cover_plan', 'Cover design plan — who will design your cover?', ['Hire a professional designer', 'Use a platform cover generator', 'Design it myself', 'Not decided yet'])}
          {renderTextArea(3, 'pricing', 'What feels right for pricing your book and why?')}
          {renderCallout("You don't need anyone's permission to publish. You need a plan and the faithfulness to execute it. The path exists. Walk it.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Day 3 Compass Entry</h3>
          <div className="bg-[#FDF6EC] border-t-4 border-[#E87722] p-6 rounded shadow-inner">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">My publishing path is...</label>
                <input type="text" value={formData[3]?.ce_path || ''} onChange={e => handleChange(3, 'ce_path', e.target.value)} onBlur={() => handleSave(3)} className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D5016] mb-1">The one decision I need to make next is...</label>
                <input type="text" value={formData[3]?.ce_decision || ''} onChange={e => handleChange(3, 'ce_decision', e.target.value)} onBlur={() => handleSave(3)} className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-[#2D5016] mb-1">My target publication window is...</label>
                <input type="text" value={formData[3]?.ce_timeframe || ''} onChange={e => handleChange(3, 'ce_timeframe', e.target.value)} onBlur={() => handleSave(3)} className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
            </div>
            <div className="p-4 bg-white border border-[#EDE0CC] rounded text-center shadow-sm">
              <p className="text-lg italic text-gray-800 font-medium">{getCompassSentences().ce3}</p>
            </div>
          </div>
        </section>

{(() => {
  const step = caretakerSurveyStep;
  const FEAR_GUIDANCE = {
    'Cost — I\'m not sure I can afford editing, cover design, etc.': { label: 'Cost', guidance: 'Indie publishing has a real upfront cost — but it\'s scalable. Start with what you can: a professional cover is the highest-ROI investment. Good editing can come from trusted beta readers early on. Build the quality over time.' },
    'Quality — what if my book doesn\'t look professional?': { label: 'Quality', guidance: 'A professional cover and clean interior formatting are 90% of perceived quality. Both are achievable at reasonable cost. Readers judge books by covers — invest there first.' },
    'Complexity — the process feels overwhelming': { label: 'Complexity', guidance: 'Break it into three decisions: path, format, platform. That\'s it. You don\'t have to figure out everything at once. The Caretaker\'s job is faithful stewardship of one step at a time.' },
    'Legitimacy — will people take a self-published book seriously?': { label: 'Legitimacy', guidance: 'The legitimacy question is settled. Indie authors sell millions of books. What makes a book credible is quality — not the publisher\'s logo on the spine. Your Emmaus Way Media Group imprint is as real as any.' },
    'Rights — I\'m confused about ISBNs, copyright, distribution': { label: 'Rights & Process', guidance: 'Copyright is automatic on creation. ISBNs are optional (platform provides free ones) or purchasable through Bowker. Start simple: KDP handles distribution. You can expand to wide distribution (Draft2Digital, IngramSpark) when ready.' },
    'Timing — I don\'t know when my book is ready to publish': { label: 'Timing', guidance: 'The book is ready when it\'s been edited, proofread, and formatted — and when you can read it from start to finish without cringing at something you know how to fix. Done is better than perfect. Ship it.' },
  };
  const KNOWLEDGE_DESCRIPTIONS = {
    'Just starting — I don\'t know what I don\'t know': 'You\'re at the beginning of a learnable path. Everything you need to know is available — and camp is the right place to start mapping it.',
    'Some research — I\'ve looked into it but I\'m not ready': 'You have a foundation. The gap between research and readiness is usually one decision — pick your path and start moving.',
    'Pretty informed — I understand the basics and have a plan': 'You\'re closer than you think. The next step is execution, not more research. Make the decisions and start.',
    'Ready to go — I know what I\'m doing, I just need to do it': 'Then do it. The Caretaker\'s job isn\'t to prepare endlessly — it\'s to steward the work faithfully into the world.',
  };
  const PRIORITY_DESCRIPTIONS = {
    'Creative control — I want full say over cover, pricing, content': 'Indie is your path. You keep every creative decision — cover, title, price, content, timeline. It\'s your book.',
    'Speed — I want to get this out as fast as reasonably possible': 'Indie again. Traditional publishing takes 2–4 years from manuscript to shelf. Indie can move in weeks once the work is ready.',
    'Credibility — I want the backing of a publisher name': 'Traditional or hybrid. Know that credibility is increasingly earned by the quality of the book itself, not the publisher\'s name.',
    'Revenue — I want the highest return per sale': 'Indie — 70% royalty on ebooks vs. 10–15% traditional. The math is clear if revenue is the priority.',
    'Reach — I want to get into as many hands as possible': 'Wide distribution through Draft2Digital + IngramSpark reaches every major retailer. Reach doesn\'t require a traditional publisher.',
  };

  const renderOptionButtons = (day, field, options) => (
    <div className="grid grid-cols-1 gap-3 mt-4">
      {options.map(opt => (
        <button key={opt} onClick={() => { handleChange(day, field, opt); handleSave(day, true); }}
          className={`p-3 text-left rounded-md border-2 transition-all font-medium text-sm ${formData[day]?.[field] === opt ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md' : 'bg-white border-gray-200 text-gray-700 hover:border-[#2D5016] hover:bg-[#EBF2E3]'}`}>
          {opt}
        </button>
      ))}
    </div>
  );
  const StepHeader = ({ stepNum, title }) => (
    <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
      <div>
        <div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Step {stepNum} of 4</div>
        <h3 className="text-2xl font-bold text-[#2D5016]">{title}</h3>
      </div>
      <button onClick={() => setCaretakerSurveyStep(0)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Profile</button>
    </div>
  );

  if (step === 0) {
    if (formData[3]?.caretaker_survey_complete) { setCaretakerSurveyStep(5); return null; }
    return (
      <div className="bg-white p-8 rounded-lg border-2 border-[#2D5016] shadow-lg mt-8 mb-6 text-center">
        <h3 className="text-2xl font-bold text-[#2D5016] mb-2 uppercase tracking-tight">Your Publishing Readiness Profile</h3>
        <p className="text-[#E87722] font-bold text-lg mb-4">Know where you stand — and what to do next.</p>
        <p className="text-gray-700 mb-6 max-w-xl mx-auto leading-relaxed">
          Four questions that go beyond path selection. Assess your readiness, name your real publishing fear, and clarify what matters most to you as a Caretaker of this work.
        </p>
        <Button onClick={() => setCaretakerSurveyStep(1)} className="bg-[#2D5016] text-white font-bold py-3 px-8 rounded text-lg shadow-md hover:bg-green-800 transition-all inline-flex items-center gap-2 mx-auto">
          Start the Profile <SafeIcon name="ArrowRight" />
        </Button>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={1} title="Your Publishing Knowledge" />
        <p className="text-gray-600 mb-8 text-lg">Honest self-awareness is where good stewardship begins. Where are you right now?</p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">How would you honestly describe your publishing knowledge level?</label>
            {renderOptionButtons(3, 'caretaker_knowledge_level', [
              'Just starting — I don\'t know what I don\'t know',
              'Some research — I\'ve looked into it but I\'m not ready',
              'Pretty informed — I understand the basics and have a plan',
              'Ready to go — I know what I\'m doing, I just need to do it',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>📚</span> You Don't Need Permission</h4>
          <p className="text-gray-700 text-sm leading-relaxed">Jeremy has published 56 books independently through Emmaus Way Media Group — fiction, nonfiction, Bible studies, audiobooks, a study Bible. The tools exist. The platforms exist. The permission exists. What's required is the willingness to learn and the faithfulness to do the work.</p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCaretakerSurveyStep(0)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCaretakerSurveyStep(2)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={2} title="Your Publishing Fear" />
        <p className="text-gray-600 mb-8 text-lg">Every author has a publishing fear. Naming it is the first step to moving past it.</p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">What is your biggest fear or obstacle when it comes to publishing?</label>
            {renderOptionButtons(3, 'caretaker_publish_fear', [
              'Cost — I\'m not sure I can afford editing, cover design, etc.',
              'Quality — what if my book doesn\'t look professional?',
              'Complexity — the process feels overwhelming',
              'Legitimacy — will people take a self-published book seriously?',
              'Rights — I\'m confused about ISBNs, copyright, distribution',
              'Timing — I don\'t know when my book is ready to publish',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>✦</span> The Caretaker Frame</h4>
          <p className="text-gray-700 text-sm leading-relaxed">You are a steward of this work — not just an author. Stewardship means doing the faithful next thing with what you've been given. The fear is real. The path through it is also real.</p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCaretakerSurveyStep(1)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCaretakerSurveyStep(3)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={3} title="Your Publishing Priority" />
        <p className="text-gray-600 mb-8 text-lg">What matters most shapes which path is actually right for you — not just which one sounds right.</p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">What is your most important priority as you publish?</label>
            {renderOptionButtons(3, 'caretaker_publish_priority', [
              'Creative control — I want full say over cover, pricing, content',
              'Speed — I want to get this out as fast as reasonably possible',
              'Credibility — I want the backing of a publisher name',
              'Revenue — I want the highest return per sale',
              'Reach — I want to get into as many hands as possible',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>📖</span> From the Handbook</h4>
          <p className="text-gray-700 text-sm leading-relaxed">"Gone are the days where you are required to print several hundred copies and store them in your basement. Print-on-demand means the book is printed and shipped when the reader orders it. No setup fees, no inventory risk. It really is a win-win situation, for you and your readers."</p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCaretakerSurveyStep(2)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => setCaretakerSurveyStep(4)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <StepHeader stepNum={4} title="Your Platform Readiness" />
        <p className="text-gray-600 mb-8 text-lg">Publishing your book is only the beginning. The Connector's work starts before the book is done. Where does your author platform stand right now?</p>
        <div className="space-y-8">
          <div>
            <label className="block font-bold text-[#2557AB] text-lg">Which best describes your current author platform?</label>
            {renderOptionButtons(3, 'caretaker_biz_readiness', [
              'I have an author website and an active email list',
              'I have a website but no email list yet',
              'I have neither — but I\'m working on it',
              'I haven\'t started thinking about platform yet',
            ])}
          </div>
        </div>
        <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-5 rounded mt-10">
          <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span>✦</span> Platform Isn't Optional</h4>
          <p className="text-gray-700 text-sm leading-relaxed">Publishing without a platform is like opening a bookstore in a field with no road. The three minimums — a website, an email list, and one consistent social presence — don't need to be perfect before you publish. They need to exist. Start now, even if the book isn't done.</p>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
          <Button variant="secondary" onClick={() => setCaretakerSurveyStep(3)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          <Button onClick={() => { handleChange(3, 'caretaker_survey_complete', true); handleSave(3, true); setCaretakerSurveyStep(5); }} className="bg-[#E87722] text-white px-8 font-bold hover:bg-[#d06b1a]">See My Publishing Profile &rarr;</Button>
        </div>
      </div>
    );
  }

  if (step === 5) {
    const d3 = formData[3] || {};
    const fearData = FEAR_GUIDANCE[d3.caretaker_publish_fear] || null;
    const knowledgeDesc = KNOWLEDGE_DESCRIPTIONS[d3.caretaker_knowledge_level] || null;
    const priorityDesc = PRIORITY_DESCRIPTIONS[d3.caretaker_publish_priority] || null;
    const platformNote = {
      'I have an author website and an active email list': 'You\'re ahead of most. Keep building — consistent content on your list is what converts readers into fans.',
      'I have a website but no email list yet': 'The email list is the most important of the three minimums. Open an account with MailerLite or ConvertKit this week and add a sign-up form to your site.',
      'I have neither — but I\'m working on it': 'Start with the website. One page, your name, what you write, a way to contact you. That\'s enough for now.',
      'I haven\'t started thinking about platform yet': 'Start today — even before the book is done. Your website and email list are the foundation everything else is built on.',
    };

    return (
      <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-8 mb-6 text-left">
        <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
          <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Your Profile</div><h3 className="text-2xl font-bold text-[#2D5016]">Your Publishing Readiness</h3></div>
          <button onClick={() => setCaretakerSurveyStep(1)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Edit Answers</button>
        </div>
        <div className="rounded-lg border border-[#EDE0CC] overflow-hidden mb-6">
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Publishing Knowledge</span></div>
          <div className="px-5 py-4 bg-white border-b border-[#EDE0CC]">
            <span className="font-bold text-[#2557AB] text-base">{d3.caretaker_knowledge_level || <span className="text-gray-400 italic font-normal text-sm">Not answered</span>}</span>
            {knowledgeDesc && <p className="text-gray-600 text-sm mt-1 leading-relaxed">{knowledgeDesc}</p>}
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Your Publishing Fear</span></div>
          <div className="px-5 py-4 bg-[#FDF6EC] border-b border-[#EDE0CC]">
            {fearData ? (<><span className="font-bold text-[#E87722] text-base">{fearData.label}</span><p className="text-gray-700 text-sm mt-1 leading-relaxed">{fearData.guidance}</p></>) : (<span className="text-gray-400 italic text-sm">Not answered</span>)}
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Your Publishing Priority</span></div>
          <div className="px-5 py-4 bg-white border-b border-[#EDE0CC]">
            <span className="font-bold text-[#2557AB] text-base">{d3.caretaker_publish_priority ? d3.caretaker_publish_priority.split(' — ')[0] : <span className="text-gray-400 italic font-normal text-sm">Not answered</span>}</span>
            {priorityDesc && <p className="text-gray-600 text-sm mt-1 leading-relaxed">{priorityDesc}</p>}
          </div>
          <div className="bg-[#2D5016] px-5 py-2"><span className="text-white font-bold text-xs uppercase tracking-widest">Platform Readiness</span></div>
          <div className="px-5 py-4 bg-[#FDF6EC]">
            <span className="font-bold text-[#2557AB] text-base">{d3.caretaker_biz_readiness ? d3.caretaker_biz_readiness.split(' — ')[0] : <span className="text-gray-400 italic font-normal text-sm">Not answered</span>}</span>
            {d3.caretaker_biz_readiness && platformNote[d3.caretaker_biz_readiness] && <p className="text-gray-600 text-sm mt-1 leading-relaxed">{platformNote[d3.caretaker_biz_readiness]}</p>}
          </div>
        </div>
        <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] p-5 rounded mb-6">
          <h4 className="font-bold text-[#2D5016] mb-1 flex items-center gap-2"><span>✦</span> This profile is saved to your Compass.</h4>
          <p className="text-gray-700 text-sm leading-relaxed">You don't need anyone's permission to publish. You need a plan and the faithfulness to walk it. The path exists. Your profile tells you where you are on it — and what your next faithful step looks like.</p>
        </div>
        <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
          <button onClick={() => setCaretakerSurveyStep(1)} className="text-sm text-gray-400 hover:text-gray-600 underline">&larr; Edit answers</button>
          <button onClick={() => setCaretakerSurveyStep(0)} className="text-sm text-gray-400 hover:text-gray-600 underline">Done</button>
        </div>
      </div>
    );
  }

  return null;
})()}

        <div className="flex items-center gap-2 text-[#2D5016] font-bold border-t border-[#EDE0CC] pt-6">
          <SafeIcon name="CheckSquare" /> Day 3 Cabin Call complete. Share your publishing path leaning in tonight's Evening Campfire.
        </div>
      </div>
    </div>
  );

  // --- MARKETING SURVEY RENDERER ---
  const renderMarketingSurvey = () => {
    const step = marketingSurveyStep;

    if (step === 0) {
      if (formData[4]?.mkt_survey_complete) { setMarketingSurveyStep(5); return null; }
      return (
        <div className="bg-white p-8 rounded-lg border-2 border-[#2D5016] shadow-lg mt-12 text-center">
          <h3 className="text-3xl font-bold text-[#2D5016] mb-2 uppercase tracking-tight">GospelWise Author Marketing Starter Guide</h3>
          <p className="text-[#E87722] font-bold text-xl mb-6">Market Your Book in 15 Minutes with 4 Simple Steps.</p>
          <p className="text-gray-700 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            In just 15 minutes, this GospelWise mini-app will help you define your message, set clear goals, pick the right strategies, and take your next faithful step—without the stress.
          </p>
          <Button onClick={() => setMarketingSurveyStep(1)} className="bg-[#2D5016] text-white font-bold py-4 px-10 rounded text-xl shadow-md hover:bg-green-800 transition-all flex items-center gap-2 mx-auto">
            Start the Interview <SafeIcon name="ArrowRight" />
          </Button>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-12 text-left">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Step 1 of 4</div><h3 className="text-2xl font-bold text-[#2D5016]">Define Your Book's Mission & Reader</h3></div>
            <button onClick={() => { setMarketingSurveyStep(0); }} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Interview</button>
          </div>
          <p className="text-gray-600 mb-8 text-lg">Before you market your book, you need to define why you wrote it and who you wrote it for.</p>
          <div className="space-y-6">
            {renderTextArea(4, 'mkt_core_message', "What is your book's core message?", 3)}
            {renderTextArea(4, 'mkt_target_reader', "Who is your book for? (Be specific!)", 3)}
            {renderTextArea(4, 'mkt_transformation', "What transformation does your book offer?", 3)}
          </div>
          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
            <Button variant="secondary" onClick={() => setMarketingSurveyStep(0)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back to Welcome</Button>
            <Button onClick={() => setMarketingSurveyStep(2)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
          </div>
        </div>
      );
    }

    if (step === 2) {
      const q1Options = ['Launch Week Bestseller', 'Build Email Audience', 'Ministry Impact', 'Speaking Opportunities', 'Multiple Goals'];
      const q2Options = ['100-500 book sales', '500-1,000 book sales', '1,000-5,000 book sales', '100 new email subscribers', '500 new email subscribers', '1,000+ new email subscribers', '10 speaking engagements', 'Partnerships with 5+ ministries', 'Other custom metric'];
      const q3Options = ['Launch Week', 'First Month', 'First Quarter', 'First 6 Months', 'Full Year'];
      const renderSelectionButtons = (field, options) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          {options.map(opt => (
            <button key={opt} onClick={() => { handleChange(4, field, opt); handleSave(4, true); }}
              className={`p-3 text-left rounded-md border-2 transition-all font-medium text-sm ${formData[4]?.[field] === opt ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md' : 'bg-white border-gray-200 text-gray-700 hover:border-[#2D5016]'}`}>
              {opt}
            </button>
          ))}
        </div>
      );
      return (
        <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-12 text-left">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Step 2 of 4</div><h3 className="text-2xl font-bold text-[#2D5016]">Define Your Launch Goals</h3></div>
            <button onClick={() => setMarketingSurveyStep(0)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Interview</button>
          </div>
          <p className="text-gray-600 mb-8 text-lg">Clear goals guide your strategy. What does success look like for your book launch?</p>
          <div className="space-y-10">
            <div><label className="block font-bold text-[#2557AB] text-lg">What's your primary launch goal?</label>{renderSelectionButtons('mkt_primary_goal', q1Options)}</div>
            <div><label className="block font-bold text-[#2557AB] text-lg">What specific metric defines success?</label>{renderSelectionButtons('mkt_success_metric', q2Options)}</div>
            <div>
              <label className="block font-bold text-[#2557AB] text-lg">What's your timeline for this goal?</label>
              <div className="flex flex-wrap gap-3 mt-4">
                {q3Options.map(opt => (
                  <button key={opt} onClick={() => { handleChange(4, 'mkt_timeline', opt); handleSave(4, true); }}
                    className={`px-4 py-2 rounded-full border-2 transition-all font-bold text-sm ${formData[4]?.mkt_timeline === opt ? 'bg-[#E87722] border-[#E87722] text-white shadow-md' : 'bg-white border-gray-200 text-[#E87722] hover:border-[#E87722]'}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-6 rounded mt-10">
            <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span className="text-xl">🎯</span> Why Goals Matter</h4>
            <p className="text-gray-700 text-sm leading-relaxed">Clear goals help you choose the right marketing strategies and measure what's working. Whether you're aiming for sales, impact, or audience growth - knowing your target makes every decision easier.</p>
          </div>
          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
            <Button variant="secondary" onClick={() => setMarketingSurveyStep(1)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
            <Button onClick={() => setMarketingSurveyStep(3)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
          </div>
        </div>
      );
    }

    if (step === 3) {
      const channelOptions = ['Email List', 'Podcasting', 'Instagram', 'Blog Post', 'Facebook', 'Speaking', 'YouTube', 'Partners', 'TikTok', 'Other'];
      const adPlatformOptions = ['Facebook', 'Amazon', 'Instagram', 'Pinterest', 'BookBub'];
      const selectedChannels = formData[4]?.mkt_channels || [];
      const handleChannelToggle = (channel) => {
        const current = formData[4]?.mkt_channels || [];
        let updated;
        if (current.includes(channel)) { updated = current.filter((c) => c !== channel); }
        else { if (current.length >= 3) return; updated = [...current, channel]; }
        handleChange(4, 'mkt_channels', updated);
        handleSave(4, true);
      };
      return (
        <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-12 text-left">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Step 3 of 5</div><h3 className="text-2xl font-bold text-[#2D5016]">Choose Your Marketing Strategy</h3></div>
            <button onClick={() => setMarketingSurveyStep(0)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Interview</button>
          </div>
          <p className="text-gray-600 mb-8 text-lg">Select 1–3 marketing methods that best match your strengths, audience, and bandwidth.</p>
          <div className="space-y-10">
            <div>
              <label className="block font-bold text-[#2557AB] text-lg mb-2">What kind of content feels natural for you to create?</label>
              <textarea value={formData[4]?.mkt_content_style || ''} onChange={(e) => handleChange(4, 'mkt_content_style', e.target.value)} onBlur={() => handleSave(4, true)} placeholder="short emails, devotional posts, video chats, teaching content…" rows={3} className="w-full border-2 border-[#EDE0CC] rounded-md p-3 text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#2557AB] resize-none text-sm leading-relaxed" />
            </div>
            <div>
              <label className="block font-bold text-[#2557AB] text-lg mb-2">What are your author strengths / gifts?</label>
              <textarea value={formData[4]?.mkt_strengths || ''} onChange={(e) => handleChange(4, 'mkt_strengths', e.target.value)} onBlur={() => handleSave(4, true)} placeholder="storytelling, encouragement, teaching, connecting with people…" rows={3} className="w-full border-2 border-[#EDE0CC] rounded-md p-3 text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#2557AB] resize-none text-sm leading-relaxed" />
            </div>
            <div>
              <label className="block font-bold text-[#2557AB] text-lg mb-1">Select 1–3 Marketing Channels</label>
              <p className="text-sm text-gray-500 mb-4">Pick what fits you best!</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {channelOptions.map((channel) => {
                  const isSelected = selectedChannels.includes(channel);
                  const isDisabled = !isSelected && selectedChannels.length >= 3;
                  return (
                    <button key={channel} onClick={() => handleChannelToggle(channel)} disabled={isDisabled}
                      className={`p-3 text-left rounded-md border-2 transition-all font-medium text-sm flex items-center gap-2 ${isSelected ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md' : isDisabled ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-700 hover:border-[#2D5016]'}`}>
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs ${isSelected ? 'bg-white border-white text-[#2D5016]' : 'border-gray-300'}`}>{isSelected && '✓'}</span>
                      {channel}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-sm font-semibold text-right">
                <span className={selectedChannels.length === 3 ? 'text-[#E87722]' : 'text-gray-400'}>Selected: {selectedChannels.length}/3</span>
                {selectedChannels.length === 3 && <span className="ml-2 text-xs text-[#E87722]">Maximum reached</span>}
              </div>
            </div>
            <div>
              <label className="block font-bold text-[#2557AB] text-lg mb-1">Choose 1 Ad Platform to Focus On</label>
              <p className="text-sm text-gray-500 mb-4">You can always expand later — start with one.</p>
              <div className="flex flex-wrap gap-3">
                {adPlatformOptions.map((platform) => (
                  <button key={platform} onClick={() => { handleChange(4, 'mkt_ad_platform', platform); handleSave(4, true); }}
                    className={`px-5 py-2 rounded-full border-2 transition-all font-bold text-sm ${formData[4]?.mkt_ad_platform === platform ? 'bg-[#E87722] border-[#E87722] text-white shadow-md' : 'bg-white border-gray-200 text-[#E87722] hover:border-[#E87722]'}`}>
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-6 rounded mt-10">
            <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span className="text-xl">🎯</span> Pick Your Lane</h4>
            <p className="text-gray-700 text-sm leading-relaxed">The best marketing strategy is the one you'll actually do consistently. Choose channels that match how you already communicate — your natural voice will carry further than a strategy that feels like a performance.</p>
          </div>
          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
            <Button variant="secondary" onClick={() => setMarketingSurveyStep(2)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
            <Button onClick={() => setMarketingSurveyStep(4)} className="bg-[#2D5016] text-white px-8">Next Step &rarr;</Button>
          </div>
        </div>
      );
    }

    if (step === 4) {
      const firstActionOptions = [
        'Write a short email about your book',
        'Post on social media about your core message',
        'Reach out to one person for support or a review',
        'Create a basic promo image in Canva',
        'Open your email platform + plan your first series',
      ];
      return (
        <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-12 text-left">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Step 4 of 5</div><h3 className="text-2xl font-bold text-[#2D5016]">Take Your First Marketing Action</h3></div>
            <button onClick={() => setMarketingSurveyStep(0)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Interview</button>
          </div>
          <p className="text-gray-600 mb-8 text-lg">Let's build momentum — without stress. Choose one doable task you can finish in under 15 minutes.</p>
          <div>
            <label className="block font-bold text-[#2557AB] text-lg mb-4">Pick one action you could do today in 15 minutes or less:</label>
            <div className="flex flex-col gap-3">
              {firstActionOptions.map((action) => {
                const isSelected = formData[4]?.mkt_first_action === action;
                return (
                  <button key={action} onClick={() => { handleChange(4, 'mkt_first_action', action); handleSave(4, true); }}
                    className={`w-full p-4 text-left rounded-md border-2 transition-all font-medium text-sm flex items-center gap-3 ${isSelected ? 'bg-[#2D5016] border-[#2D5016] text-white shadow-md' : 'bg-white border-gray-200 text-gray-700 hover:border-[#2D5016] hover:bg-[#EBF2E3]'}`}>
                    <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isSelected ? 'border-white bg-white' : 'border-gray-300 bg-white'}`}>
                      {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#2D5016] block" />}
                    </span>
                    {action}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="bg-[#EBF2E3] border-l-4 border-[#2D5016] p-6 rounded mt-10">
            <h4 className="font-bold text-[#2D5016] mb-2 flex items-center gap-2 text-base"><span className="text-xl">🙌</span> Remember: You've Got This!</h4>
            <p className="text-gray-700 text-sm leading-relaxed">Marketing happens one step at a time. Choose your first step in connecting your important creative work to people who need it.</p>
          </div>
          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between">
            <Button variant="secondary" onClick={() => setMarketingSurveyStep(3)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
            <Button onClick={() => { handleChange(4, 'mkt_survey_complete', true); handleSave(4, true); setMarketingSurveyStep(5); }} className="bg-[#E87722] text-white px-8 font-bold hover:bg-[#d06b1a]">Give Me My Plan &rarr;</Button>
          </div>
        </div>
      );
    }

    if (step === 5) {
      const val = (v) => v && String(v).trim() ? v : <span className="text-gray-300 italic">Not answered</span>;
      const arrVal = (v) => Array.isArray(v) && v.length > 0 ? v.join(', ') : <span className="text-gray-300 italic">Not answered</span>;
      const summaryRows = [
        { section: 'Your Book', label: 'Core message', value: val(formData[4]?.mkt_core_message) },
        { section: 'Your Book', label: 'Target reader', value: val(formData[4]?.mkt_target_reader) },
        { section: 'Your Book', label: 'Transformation', value: val(formData[4]?.mkt_transformation) },
        { section: 'Launch Goals', label: 'Primary goal', value: val(formData[4]?.mkt_primary_goal) },
        { section: 'Launch Goals', label: 'Success metric', value: val(formData[4]?.mkt_success_metric) },
        { section: 'Launch Goals', label: 'Timeline', value: val(formData[4]?.mkt_timeline) },
        { section: 'Marketing Strategy', label: 'Content style', value: val(formData[4]?.mkt_content_style) },
        { section: 'Marketing Strategy', label: 'Author strengths', value: val(formData[4]?.mkt_strengths) },
        { section: 'Marketing Strategy', label: 'Channels', value: arrVal(formData[4]?.mkt_channels) },
        { section: 'Marketing Strategy', label: 'Ad platform', value: val(formData[4]?.mkt_ad_platform) },
        { section: 'First Action', label: 'Action in 15 min', value: val(formData[4]?.mkt_first_action) },
      ];
      const sections = [...new Set(summaryRows.map((r) => r.section))];
      return (
        <div className="bg-white p-8 rounded-lg border border-[#EDE0CC] shadow-sm mt-12 text-left">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <div><div className="text-xs font-bold text-[#E87722] uppercase tracking-widest mb-1">Your Plan</div><h3 className="text-2xl font-bold text-[#2D5016]">Review Your Marketing Plan</h3></div>
            <button onClick={() => setMarketingSurveyStep(0)} className="text-sm text-gray-500 hover:text-gray-800 underline font-medium">Exit Interview</button>
          </div>
          <p className="text-gray-600 mb-8 text-lg">Here's everything you've mapped out. Review it, go back to edit anything, then save your marketing plan to your Compass.</p>
          <div className="rounded-lg border border-[#EDE0CC] overflow-hidden mb-8">
            {sections.map((section, si) => {
              const rows = summaryRows.filter((r) => r.section === section);
              return (
                <div key={section}>
                  <div className="bg-[#2D5016] px-5 py-3"><span className="text-white font-bold text-sm uppercase tracking-widest">{section}</span></div>
                  {rows.map((row, ri) => (
                    <div key={row.label} className={`flex gap-4 px-5 py-4 text-sm ${ri % 2 === 0 ? 'bg-white' : 'bg-[#FDF6EC]'} ${ri < rows.length - 1 ? 'border-b border-[#EDE0CC]' : ''}`}>
                      <span className="font-semibold text-[#2557AB] w-44 flex-shrink-0">{row.label}</span>
                      <span className="text-gray-700 flex-1">{row.value}</span>
                    </div>
                  ))}
                  {si < sections.length - 1 && <div className="h-1 bg-[#E87722] opacity-30" />}
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 flex-wrap mb-8 text-sm">
            <span className="text-gray-500 font-medium">Edit a section:</span>
            {[
              { label: 'Your Book', step: 1 },
              { label: 'Launch Goals', step: 2 },
              { label: 'Marketing Strategy', step: 3 },
              { label: 'First Action', step: 4 },
            ].map(({ label, step: s }) => (
              <button key={label} onClick={() => setMarketingSurveyStep(s)} className="text-[#2557AB] underline hover:text-[#2D5016] font-medium">{label}</button>
            ))}
          </div>
          <div className="bg-[#FDF6EC] border-l-4 border-[#2557AB] p-6 rounded mb-10">
            <h4 className="font-bold text-[#2557AB] mb-1 flex items-center gap-2"><span className="text-xl">✦</span> Your plan is ready.</h4>
            <p className="text-gray-700 text-sm leading-relaxed">This marketing plan is saved to your Day 4 Compass entry. Return to it anytime from your Compass — and remember, the best marketing plan is the one you actually start. Pick one channel, show up consistently, and let your voice do the work.</p>
          </div>
          <div className="mt-2 pt-6 border-t border-gray-100 flex justify-between">
            <Button variant="secondary" onClick={() => setMarketingSurveyStep(4)} className="border-gray-300 text-gray-600 hover:bg-gray-50">&larr; Back</Button>
          </div>
        </div>
      );
    }

    return null;
  };

  // --- DAY 4 RENDER ---
  const renderDay4 = () => (
    <div className="bg-[#FDF6EC] rounded-lg font-poppins shadow-sm border border-[#EDE0CC]">
      <div className="bg-[#2D5016] text-white font-bold px-6 py-3 rounded-t-lg tracking-widest uppercase">
        DAY 4 — THE CONNECTOR · MARKETING
      </div>
      <div className="p-6 md:p-8 bg-white rounded-b-lg space-y-8">
        <p className="text-gray-700 leading-relaxed text-lg">Selling is serving. Marketing is connecting your book to a reader who needs it. When you frame it that way — not as self-promotion, not as interruption — it becomes one of the most loving things you can do with the work God gave you to write.</p>
        
        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your One Reader</h3>
          <p className="text-sm text-gray-600 mb-4">Not an 'ideal reader demographic.' One specific person. Give them a name if it helps.</p>
          {renderInput(4, 'reader_name', 'Give your reader a name — real or representative:')}
          {renderTextArea(4, 'reader_life', 'Describe their life in 2–3 sentences. Who are they? What is their daily reality?')}
          {renderTextArea(4, 'reader_struggle', 'What are they struggling with that your book speaks directly into?', 3)}
          {renderTextArea(4, 'reader_hope', 'What do you hope God does in their life through your book?', 3)}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Marketing Minimums</h3>
          {renderRadio(4, 'website_status', 'Website status:', ['Have one and it represents me well', 'Have one but it needs work', 'Don\'t have one yet'])}
          {renderRadio(4, 'email_status', 'Email list status:', ['Have a list and email them regularly', 'Have a list but rarely email them', 'Starting from scratch'])}
          {renderRadio(4, 'social_status', 'Social media presence:', ['Active and engaged on at least one platform', 'Sporadic posting', 'Prefer not to use social media'])}
          {renderTextArea(4, 'marketing_first_minimum', 'Based on your answers, which minimum do you need to build or strengthen first?')}
          {renderCallout("You don't have to be everywhere. You have to be somewhere, consistently, being yourself. Pick one channel to own before adding a second.")}
        </section>

        <section>
          <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Day 4 Compass Entry</h3>
          <div className="bg-[#FDF6EC] border-t-4 border-[#E87722] p-6 rounded shadow-inner">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-[#2D5016] mb-1">My first marketing move is...</label>
                <input type="text" value={formData[4]?.marketing_first_minimum || ''} onChange={e => handleChange(4, 'marketing_first_minimum', e.target.value)} onBlur={() => handleSave(4)} placeholder="e.g. Set up my email list" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-[#2D5016] mb-1">I will start by...</label>
                <input type="text" value={formData[4]?.marketing_first_content || ''} onChange={e => handleChange(4, 'marketing_first_content', e.target.value)} onBlur={() => handleSave(4)} placeholder="e.g. Creating a simple landing page" className="w-full border border-[#EDE0CC] bg-white p-2 rounded focus:border-[#E87722] outline-none" />
              </div>
            </div>
            <div className="p-4 bg-white border border-[#EDE0CC] rounded text-center shadow-sm">
              <p className="text-lg italic text-gray-800 font-medium">{getCompassSentences().ce4}</p>
            </div>
          </div>
        </section>

        {/* Marketing Survey Section */}
        {renderMarketingSurvey()}

        <div className="flex items-center gap-2 text-[#2D5016] font-bold border-t border-[#EDE0CC] pt-6 mt-8">
          <SafeIcon name="CheckSquare" /> Day 4 Cabin Call complete. Share your one reader in tonight's Evening Campfire.
        </div>
      </div>
    </div>
  );

  // --- DAY 5 RENDER ---
  const renderDay5 = () => {
    const s = getCompassSentences();
    return (
      <div className="bg-[#FDF6EC] rounded-lg font-poppins shadow-sm border border-[#EDE0CC]">
        <div className="bg-[#2D5016] text-white font-bold px-6 py-4 rounded-t-lg tracking-widest uppercase">
          DAY 5 — THE COMPASS · DIRECTION
        </div>
        <div className="p-6 md:p-8 bg-white rounded-b-lg space-y-8">
          <p className="text-gray-700 leading-relaxed text-lg">You've done the work. You've clarified your calling, defined your writing rhythm, chosen your publishing path, and focused your marketing. Now, it's time to bring it all together. This is your GospelWise Author Compass.</p>
          
          <div className="space-y-6">
            <div className="bg-[#EBF2E3] p-5 rounded border-l-4 border-[#2D5016]">
              <h4 className="font-bold text-[#2D5016] text-lg mb-2">The Christian — Calling Clarity</h4>
              <p className="text-gray-800 italic">{s.ce1}</p>
            </div>
            <div className="bg-[#EBF2E3] p-5 rounded border-l-4 border-[#2D5016]">
              <h4 className="font-bold text-[#2D5016] text-lg mb-2">The Creator — Writing Clarity</h4>
              <p className="text-gray-800 italic">{s.ce2}</p>
            </div>
            <div className="bg-[#EBF2E3] p-5 rounded border-l-4 border-[#2D5016]">
              <h4 className="font-bold text-[#2D5016] text-lg mb-2">The Caretaker — Publishing Clarity</h4>
              <p className="text-gray-800 italic">{s.ce3}</p>
            </div>
            <div className="bg-[#EBF2E3] p-5 rounded border-l-4 border-[#2D5016]">
              <h4 className="font-bold text-[#2D5016] text-lg mb-2">The Connector — Marketing Clarity</h4>
              <p className="text-gray-800 italic">{s.ce4}</p>
            </div>
          </div>

          <section className="mt-10 border-t border-[#EDE0CC] pt-8">
            <h3 className="text-[#2D5016] font-bold text-xl mb-6 bg-[#EBF2E3] px-4 py-2 border-l-4 border-[#2D5016]">Your Next Right Step</h3>
            <p className="text-gray-700 mb-6">You don't have to do it all today. You just have to do the next right thing.</p>
            {renderTextArea(5, 'one_thing', 'What is the ONE faithful step you need to take next week to move this project forward?')}
            {renderInput(5, 'final_date', 'By what specific date will you take this step?')}
            {renderInput(5, 'final_accountability', 'Who will hold you accountable to this?')}
            {renderTextArea(5, 'final_meaning', 'Look at your Compass. If you actually finish this book and get it to your reader — what would that mean to you?', 3)}
          </section>

          <div className="bg-[#FDF6EC] border-l-4 border-[#E87722] p-6 rounded mt-8">
            <h4 className="font-bold text-[#E87722] text-lg mb-2">Download Your Compass Summary</h4>
            <p className="text-gray-800 mb-4">Export your complete GospelWise Author Compass into a beautifully formatted PDF for long-term guidance.</p>
            <Button onClick={generateCompassPDF} className="bg-[#2D5016] text-white hover:bg-green-800 transition-colors shadow-md">
              <SafeIcon name="Download" className="inline mr-2" /> Export Your Compass
            </Button>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded mt-8">
            <h4 className="font-bold text-blue-900 text-lg mb-2">Download Your Full Workbook</h4>
            <p className="text-blue-800 mb-4">Export all of your answers from the week into a beautifully formatted PDF.</p>
            <Button onClick={generateWorkbookPDF} disabled={!isAAP} className={`${!isAAP ? 'bg-gray-300 text-gray-500' : 'bg-[#2557AB] text-white hover:bg-blue-800 transition-colors shadow-md'}`}>
              <SafeIcon name="Download" className="inline mr-2" /> Download Complete Workbook
            </Button>
            {!isAAP && (
              <p className="text-sm text-gray-500 mt-2">Want to keep your workbook answers after camp? Workbook export is an All-Access perk — upgrade before camp ends.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const dayTitles = {
    1: 'The Christian',
    2: 'The Creator',
    3: 'The Caretaker',
    4: 'The Connector',
    5: 'Compass Summary'
  };

  if (loading) {
    return (
      <div className="p-8 max-w-4xl mx-auto flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <SafeIcon name="Loader" className="w-8 h-8 animate-spin mx-auto text-[#2D5016] mb-4" />
          <p className="text-gray-600 font-poppins">Opening the Cabin...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 font-poppins relative">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[#2D5016]">The Compass</h1>
          <p className="text-gray-600 text-lg">Your 5-Day Workbook</p>
        </div>
        {user.role === 'admin' && (
          <label className="flex items-center gap-2 text-sm bg-gray-100 p-2 rounded cursor-pointer border">
            <input type="checkbox" checked={devMode} onChange={e => setDevMode(e.target.checked)} />
            Admin: Unlock All
          </label>
        )}
      </div>

      {/* Marker for sticky scroll */}
      <div ref={topRef} className="absolute top-0 left-0 w-full h-1" />

      {/* Sticky Header */}
      <div className={`transition-all duration-300 z-40 bg-white border-[#EDE0CC] flex items-center justify-between shadow-sm py-4
        ${isScrolled ? 'fixed top-0 left-0 right-0 w-full border-b px-4 md:px-8' : 'relative rounded-lg mb-6 border px-6'}`}
      >
        <div className={`w-full mx-auto flex items-center justify-between ${isScrolled ? 'max-w-7xl' : ''}`}>
          <div>
            <div className="text-xs font-bold text-[#E87722] uppercase tracking-wider">Day {activeDay}</div>
            <h2 className="text-xl font-bold text-[#2D5016] leading-tight">{dayTitles[activeDay]}</h2>
          </div>
        </div>
      </div>
      {isScrolled && <div className="h-[76px] mb-0 lg:mb-6" />}

      <div className="flex flex-col lg:flex-row gap-8 relative items-start">
        {/* Sidebar */}
        <div className="w-full lg:w-1/3 lg:sticky lg:top-[100px] z-30">
          <Card className="p-4 bg-white shadow-sm border border-[#EDE0CC]">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="font-bold text-[#2D5016] uppercase tracking-wider text-sm">Compass Days</h3>
              {saveStatus && (
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex items-center gap-1">
                  {saveStatus === 'Saving...' ? <SafeIcon name="RefreshCw" className="w-3 h-3 animate-spin"/> : <SafeIcon name="Check" className="w-3 h-3"/>}
                  {saveStatus}
                </span>
              )}
            </div>
            {renderNavLinks()}
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="w-full lg:w-2/3">
          {isLockedOut ? (
            <Card className="text-center py-16 animate-fadeIn">
              <SafeIcon name="Lock" className="text-5xl text-[#E87722] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[#2D5016] mb-2">Cabin Access Expired</h3>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto whitespace-pre-wrap">
                {settings.replay_expiry_msg || "Free pass access to the Cabin Call workbook has expired. Upgrade to All-Access to keep your Compass and every workbook answer for good."}
              </p>
              <a href="/#/upgrade">
                <button className="bg-[#E87722] text-white font-bold px-8 py-3 rounded hover:bg-orange-600 transition-colors">
                  Upgrade to All-Access
                </button>
              </a>
            </Card>
          ) : isDayLocked(activeDay) ? (
            <div className="flex flex-col items-center justify-center text-center py-20 px-8">
              <div className="w-20 h-20 bg-[#EDE0CC] rounded-full flex items-center justify-center mb-6">
                <SafeIcon name="Lock" className="w-9 h-9 text-[#2D5016]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2D5016] mb-2">Cabin Call Locked</h2>
              <p className="text-gray-600 text-base max-w-sm leading-relaxed">
                This workbook session unlocks at <strong>7:00 AM</strong> on your camp day.
                Come back then to complete Day {activeDay} — {dayTitles[activeDay]}.
              </p>
              <p className="text-gray-400 text-sm mt-4 italic">
                "Available and faithful. That's the whole assignment."
              </p>
            </div>
          ) : (
            <>
              {!isAAP && (
                <div className="mb-6 bg-[#FDF6EC] border border-[#E87722] rounded-lg p-4 flex items-start gap-3">
                  <SafeIcon name="Info" className="w-5 h-5 text-[#E87722] shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700 leading-relaxed">
                    <span className="font-bold text-[#2D5016]">Work through every question — all free during camp.</span>{' '}
                    Your Compass is yours to download and keep. Exporting your full workbook answers is an All-Access perk. Heads up: free access to the Cabin closes{' '}
                    <strong>{settings.replay_expiry
                      ? new Date(settings.replay_expiry).toLocaleString('en-US', {
                          weekday: 'long', month: 'long', day: 'numeric',
                          hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                        })
                      : 'when camp replays expire'}</strong>, so finish + download your Compass before then — or{' '}
                    <a href="/#/upgrade" className="text-[#2557AB] font-bold underline hover:text-blue-800">
                      upgrade to All-Access
                    </a>{' '}
                    to keep everything, for good.
                  </div>
                </div>
              )}
              {activeDay === 1 && renderDay1()}
              {activeDay === 2 && renderDay2()}
              {activeDay === 3 && renderDay3()}
              {activeDay === 4 && renderDay4()}
              {activeDay === 5 && renderDay5()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}